import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import OrgModuleLayout from '../OrgModuleLayout';
import OrgModulePlaceholder from '../OrgModulePlaceholder';
import { useActiveOrgDisplayName, useActiveOrgId } from '@app/hooks/useActiveOrg';
import { useAuthFetch } from '@app/hooks/useAuthFetch';
import { useTopologyApi, useVisibilityApi } from '@app/hooks/useApis';
import { useConfig } from '@config/provider';
import { ApiResponseError, buildOrgUrl, resolveBasePath } from '@app/utils/api';
import { formatConfidencePercent, getConfidenceBadgeClass } from '@app/utils/confidence';
import { streamSse } from '@app/utils/sse';
import Drawer from '@app/components/Drawer';
import DeleteConfirmationModal from '@app/components/DeleteConfirmationModal';
import StatusToast, { type StatusToastTone } from '@app/components/StatusToast';
import { ActivityIcon, ChatIcon, ExpandIcon, GraphIcon, LayersIcon, PinIcon, SaveIcon, TrashIcon } from '@app/components/icons';
import type {
  Site,
  TopologyArtifactOverlayLayerStatusschema,
  SitesListResponse,
  VisibilityProposal,
  VisibilitySession,
  TopologyV2Drilldownschema,
  TopologyV2Edgeschema,
  TopologyV2Graphschema,
  TopologyV2Legendschema,
  TopologyV2LegendNodeRulesInner,
  TopologyV2Nodeschema,
  TopologyV2ParamOptionItemschema,
  TopologyV2ParamSpecschema,
  TopologyV2ResolveRequestPatchschema,
  TopologyV2ResolveResponse,
  TopologyV2ViewCatalogItemschema,
  TopologyV2ViewCreateRequest,
  TopologyV2ViewSaveResponse,
  TopologyV2ViewScopeModeschema,
  TopologyV2ViewUpdateRequest,
  TopologyViewPatchOpschema,
  TopologyViewStreamFrame
} from '@app/types/sdk';
import { ResponseError } from '@app/types/sdk';
import {
  TopologyV2ResolveResponseFromJSON,
  TopologyViewStreamFrameFromJSON
} from '@reap/openapi-public-v1';
import {
  applyTopologyOverlayStatus,
  deriveTopologyOverlayPresentation,
  emptyTopologyOverlayState,
  setTopologyOverlayLayerStatuses,
  setTopologyLayerLocalVisibility,
  type TopologyOverlayRuntimeState
} from '@app/utils/topologyOverlay';
import { topologyLayerStatusLabel, topologyLayerStatusTitle } from '@app/utils/topologyLayerStatus';
import TopologyWorkbenchChatPanel from './TopologyWorkbenchChatPanel';
import {
  buildTopologyWorkbenchBindingDraft,
  buildTopologyWorkbenchBase,
  buildTopologyWorkbenchChatInteractContext,
  buildTopologyWorkbenchResumeKey,
  buildTopologyWorkbenchSelection,
  clearTopologyWorkbenchResumeState,
  deriveVisibilitySessionOverlayState,
  formatTopologyPathLayerMetadata,
  formatTopologyPathRelatedViewBreadcrumb,
  formatTopologyRelationshipLayerMetadata,
  mergeVisibilitySessionStateEvent,
  parseTopologyWorkbenchStreamFrame,
  readTopologyWorkbenchResumeState,
  selectTopologyWorkbenchGraph,
  shouldPreserveWorkbenchRuntimeGraph,
  shouldBlockTopologyWorkbenchSwitch,
  type TopologyWorkbenchSelectedElement,
  type VisibilityOverlayStatusEventData,
  type VisibilitySessionStateEventData,
  visibilitySessionStateToOverlayState,
  writeTopologyWorkbenchResumeState
} from './topologyWorkbench';
import { extractTopologyEmbeddedPolicySection } from './policyPresentation';
import {
	topologySelectionPropertyRows,
	topologyPresentationProps,
  topologyPresentationString
} from '@app/components/topology/presentation';
import TopologyGraphRenderer from '@app/components/topology/TopologyGraphRenderer';
import {
  applyTopologyOpsToGraph,
  extractTopologySnapshot,
  resolveTopologyFrameType,
  type TopologyStreamGraph,
  type TopologyStreamResolveResponse
} from './topologyStream';
import {
  getTopologyViewBoundScope,
  hydrateResolvedTopologyViewOverlayState,
  isCustomTopologyView,
  partitionTopologyViews,
  pickTopologyViewAfterDelete,
  removeTopologyView,
  topologyViewSaveSummaryLines,
  upsertTopologyView
} from './topologyCustomViews';

type TopologyV2Node = TopologyV2Nodeschema;
type TopologyV2Edge = TopologyV2Edgeschema;
type TopologyV2Graph = TopologyV2Graphschema;
type TopologyV2LegendRule = TopologyV2LegendNodeRulesInner;
type TopologyV2Legend = TopologyV2Legendschema;
type TopologyV2ResolveRequestPatch = TopologyV2ResolveRequestPatchschema;
type TopologyV2Drilldown = TopologyV2Drilldownschema;
type TopologyV2ParamSpec = TopologyV2ParamSpecschema;
type TopologyV2ParamOptionItem = TopologyV2ParamOptionItemschema;
type TopologyV2ViewCatalogItem = TopologyV2ViewCatalogItemschema;
type TopologyV2SseFrame = TopologyViewStreamFrame;
type TopologyV2SseOp = TopologyViewPatchOpschema;
type TimeMode = 'live' | 'fixed';
type WorkbenchSwitchRequest = {
  viewId: string;
  scopeType: string;
  scopeId: string;
  params: Record<string, ParamValue>;
  limits: Record<string, unknown>;
  asOf?: string | null;
};

const DEFAULT_CHAT_PANE_WIDTH = 420;
const MIN_CHAT_PANE_WIDTH = 340;
const MIN_TOPOLOGY_PANE_WIDTH = 520;
const MOCK_TOPOLOGY_ATTACHMENT_THREAD_ID = 'chat-topology-preview';
const MOCK_TOPOLOGY_ATTACHMENT_VIEW_ID = 'physical_overview';
const MOCK_TOPOLOGY_ATTACHMENT_SCOPE_ID = 'site-nyc';
const MOCK_TOPOLOGY_ATTACHMENT_AS_OF = '2026-05-06T12:00:00.000Z';

const formatDateTimeLocalInput = (value?: string | Date | null): string => {
  if (!value) {
    return '';
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const pad = (num: number) => num.toString().padStart(2, '0');
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

const toIsoString = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const date = new Date(trimmed);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const formatAsOfStatusLabel = (value: string | null): string => {
  if (!value) {
    return 'As of …';
  }
  const formatted = formatDateTimeLocalInput(value);
  return `As of ${formatted ? formatted.replace('T', ' ') : value}`;
};

const TOPOLOGY_ACTION_ABSOLUTE_URL_PATTERN = /^[a-z][a-z0-9+.-]*:/i;

const topologyLayerDeliveryLabel = (
  layer: TopologyOverlayRuntimeState['layers'][number],
  layerStatus?: TopologyArtifactOverlayLayerStatusschema
): string => {
  const raw = (layer.runtime?.deliveryMode ?? layerStatus?.deliveryMode ?? '').trim().toLowerCase();
  switch (raw) {
    case 'live':
      return 'Live';
    case 'snapshot':
    case 'fixed':
      return 'Fixed';
    default:
      return '';
  }
};

const topologyLayerDeliveryTone = (label: string): 'live' | 'fixed' | 'neutral' => {
  switch (label.trim().toLowerCase()) {
    case 'live':
      return 'live';
    case 'fixed':
      return 'fixed';
    default:
      return 'neutral';
  }
};

const topologyLayerDeliveryIcon = (label: string) => {
  switch (label.trim().toLowerCase()) {
    case 'live':
      return <ActivityIcon size={11} className="chat-artifact-topology__layer-delivery-icon" aria-hidden="true" />;
    case 'fixed':
      return <PinIcon size={11} className="chat-artifact-topology__layer-delivery-icon" aria-hidden="true" />;
    default:
      return null;
  }
};

const nodeKindFromRefs = (node?: TopologyV2Node): string | undefined => {
  if (!node?.refs || node.refs.length === 0) {
    return undefined;
  }
  for (const ref of node.refs) {
    const refType = typeof ref?.type === 'string' ? ref.type.trim().toLowerCase() : '';
    if (!refType.startsWith('aws.')) {
      continue;
    }
    const kind = refType.slice('aws.'.length).trim();
    if (kind) {
      return kind;
    }
  }
  return undefined;
};

const nodeLabelValue = (node: TopologyV2Node | undefined, labelKey: string): unknown => {
  const key = labelKey.trim();
  if (!key) {
    return undefined;
  }
  const direct = node?.labels?.[key];
  if (direct !== undefined) {
    return direct;
  }
  if (key === 'kind') {
    return nodeKindFromRefs(node);
  }
  return undefined;
};

const topologyPropertyValue = (properties: Record<string, unknown> | undefined, propertyKey: string): unknown => {
  const key = propertyKey.trim();
  if (!key || !properties) {
    return undefined;
  }
  if (Object.prototype.hasOwnProperty.call(properties, key)) {
    return properties[key];
  }
  const path = key.split('.').map((part) => part.trim()).filter((part) => part.length > 0);
  if (path.length === 0) {
    return undefined;
  }
  let current: unknown = properties;
  for (const part of path) {
    if (!current || typeof current !== 'object' || Array.isArray(current)) {
      return undefined;
    }
    current = (current as Record<string, unknown>)[part];
  }
  return current;
};

export const topologyLegendWhenMatches = (
  when: Record<string, unknown> | undefined,
  contextMeta: Record<string, unknown>,
  node?: TopologyV2Node,
  edge?: TopologyV2Edge
): boolean => {
  if (!when || Object.keys(when).length === 0) {
    return true;
  }

  const getValue = (key: string): unknown => {
    const k = key.trim();
    if (k === 'node.id') {
      return node?.id;
    }
    if (k === 'edge.id') {
      return edge?.id;
    }
    if (k === 'edge.fromId') {
      return edge?.fromId;
    }
    if (k === 'edge.toId') {
      return edge?.toId;
    }
    if (k.startsWith('labels.')) {
      const labelKey = k.slice('labels.'.length);
      return node ? nodeLabelValue(node, labelKey) : edge ? edge.labels?.[labelKey] : undefined;
    }
    if (k.startsWith('properties.')) {
      const propertyKey = k.slice('properties.'.length);
      return node ? topologyPropertyValue(node.properties, propertyKey) : edge ? topologyPropertyValue(edge.properties, propertyKey) : undefined;
    }
    if (k.startsWith('node.labels.')) {
      return nodeLabelValue(node, k.slice('node.labels.'.length));
    }
    if (k.startsWith('node.properties.')) {
      return topologyPropertyValue(node?.properties, k.slice('node.properties.'.length));
    }
    if (k.startsWith('edge.labels.')) {
      return edge?.labels?.[k.slice('edge.labels.'.length)];
    }
    if (k.startsWith('edge.properties.')) {
      return topologyPropertyValue(edge?.properties, k.slice('edge.properties.'.length));
    }
    if (k.startsWith('context.')) {
      const ctxKey = k.slice('context.'.length);
      return contextMeta?.[ctxKey];
    }
    return undefined;
  };

  return Object.entries(when).every(([key, expected]) => {
    const actual = getValue(key);
    if (Array.isArray(expected)) {
      return expected.some((item) => String(item) === String(actual ?? ''));
    }
    return String(expected) === String(actual ?? '');
  });
};

type ParamValue = string | boolean;

interface ParamOptionsState {
  items: TopologyV2ParamOptionItem[];
  loading: boolean;
  error: string | null;
  blocked: boolean;
  emptyLabel?: string | null;
}

interface DependentParamState {
  parent: string;
  itemsByParentValue: Record<string, TopologyV2ParamOptionItem[]>;
  supportsByParentValue: Record<string, boolean>;
  asOf: string | null;
}

const GROUPED_GRAPH_PARAM_OPTIONS_SOURCE = 'grouped_distinct_ids_from_graph';
const DEPENDENT_PARAM_STATE_SOURCE = 'parent_param_state';

const paramOptionsSource = (spec: TopologyV2ParamSpec): string => {
  const options = asRecord(spec.options);
  return typeof options?.source === 'string' ? options.source.trim() : '';
};

const isGroupedParentOptionsParam = (spec: TopologyV2ParamSpec): boolean =>
  paramOptionsSource(spec) === GROUPED_GRAPH_PARAM_OPTIONS_SOURCE;

const isDependentParamStateParam = (spec: TopologyV2ParamSpec): boolean => paramOptionsSource(spec) === DEPENDENT_PARAM_STATE_SOURCE;

const asRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  return value as Record<string, unknown>;
};

const getString = (value: unknown): string | undefined => (typeof value === 'string' ? value : undefined);

const getNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return undefined;
    }
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

const getBoolean = (value: unknown): boolean =>
  value === true || (typeof value === 'string' && value.trim().toLowerCase() === 'true');

const getOptionalBoolean = (value: unknown): boolean | null => {
  if (value === true || value === false) {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') {
      return true;
    }
    if (normalized === 'false') {
      return false;
    }
  }
  return null;
};

const getStringArray = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const items = value.filter((item): item is string => typeof item === 'string');
  return items.length > 0 ? items : undefined;
};

const buildResolveKey = (input: {
  viewId: string;
  scopeType: string;
  scopeId: string;
  params: Record<string, unknown>;
  limits: Record<string, unknown>;
  asOf?: string | null;
}): string =>
  JSON.stringify({
    viewId: input.viewId,
    scopeType: input.scopeType,
    scopeId: input.scopeId,
    params: input.params,
    limits: input.limits,
    asOf: input.asOf ?? null
  });

const sanitizeResolveLimits = (value: unknown): Record<string, unknown> => {
  const limits = asRecord(value);
  if (!limits) {
    return {};
  }
  const out: Record<string, unknown> = {};
  const allowedKeys = ['limitNodes', 'limitEdges', 'rootNodeId', 'hops'] as const;
  for (const key of allowedKeys) {
    if (key in limits) {
      out[key] = limits[key];
    }
  }
  return out;
};

const sanitizeResolveRequestPayload = (value: Record<string, unknown>): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  if (typeof value.scopeType === 'string') {
    out.scopeType = value.scopeType;
  }
  if (typeof value.scopeId === 'string') {
    out.scopeId = value.scopeId;
  }
  if (typeof value.asOf === 'string') {
    out.asOf = value.asOf;
  }
  if (asRecord(value.params)) {
    out.params = value.params;
  }
  out.limits = sanitizeResolveLimits(value.limits);
  return out;
};

const scopeTypesForView = (view?: TopologyV2ViewCatalogItem | null): string[] =>
  (view?.scopeTypes ?? []).map((scope) => String(scope));

const isDrilldownOnlyParam = (viewId: string | undefined, paramName: string): boolean => {
  const normalizedViewId = (viewId ?? '').trim().toLowerCase();
  const normalizedParamName = paramName.trim();
  if (normalizedParamName !== 'switchDeviceId') {
    return false;
  }
  return normalizedViewId === 'endpoint_overview' || normalizedViewId === 'wireless_overview';
};

const isHiddenViewParam = (spec: TopologyV2ParamSpec): boolean => {
  const hidden = (spec.ui as { hidden?: unknown } | undefined)?.hidden;
  return hidden === true || hidden === 'true';
};

const visibleViewParams = (view?: TopologyV2ViewCatalogItem | null): Array<[string, TopologyV2ParamSpec]> =>
  Object.entries(view?.params ?? {}).filter(([name, spec]) => !isDrilldownOnlyParam(view?.viewId, name) && !isHiddenViewParam(spec));

const viewParamLabel = (name: string, spec: TopologyV2ParamSpec): string => {
  const configured = typeof spec.ui?.label === 'string' ? spec.ui.label.trim() : '';
  return configured || name;
};

const viewParamOrder = (spec: TopologyV2ParamSpec): number | null => {
  const raw = (spec.ui as { order?: unknown } | undefined)?.order;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string') {
    const parsed = Number(raw.trim());
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
};

const optionParamDependencies = (paramName: string, spec: TopologyV2ParamSpec): string[] => {
  const directDependsOn = (spec as { dependsOn?: unknown }).dependsOn;
  const specRecord = asRecord(spec as unknown);
  if (!specRecord) {
    if (typeof directDependsOn === 'string') {
      const value = directDependsOn.trim();
      return value && value !== paramName ? [value] : [];
    }
    if (Array.isArray(directDependsOn)) {
      return directDependsOn
        .map((value) => String(value ?? '').trim())
        .filter((value) => value && value !== paramName)
        .sort();
    }
    return [];
  }
  const deps = new Set<string>();
  const rawDependsOn = directDependsOn ?? specRecord.dependsOn;
  if (typeof rawDependsOn === 'string') {
    const value = rawDependsOn.trim();
    if (value && value !== paramName) {
      deps.add(value);
    }
  } else if (Array.isArray(rawDependsOn)) {
    rawDependsOn.forEach((value) => {
      const name = String(value ?? '').trim();
      if (name && name !== paramName) {
        deps.add(name);
      }
    });
  }
  return Array.from(deps).sort();
};

const sortVisibleViewParams = (
  view: TopologyV2ViewCatalogItem | null | undefined,
  entries: Array<[string, TopologyV2ParamSpec]>
): Array<[string, TopologyV2ParamSpec]> => {
  if (!view || entries.length < 2) {
    return entries;
  }

  const specByName = new Map(entries);
  const indexByName = new Map<string, number>();
  entries.forEach(([name], index) => indexByName.set(name, index));

  const depthMemo = new Map<string, number>();
  const stack = new Set<string>();
  const depthFor = (name: string): number => {
    if (depthMemo.has(name)) {
      return depthMemo.get(name) ?? 0;
    }
    if (stack.has(name)) {
      return 0;
    }
    stack.add(name);
    const spec = specByName.get(name);
    const depth = spec
      ? optionParamDependencies(name, spec).reduce((max, dependency) => Math.max(max, depthFor(dependency) + 1), 0)
      : 0;
    stack.delete(name);
    depthMemo.set(name, depth);
    return depth;
  };

  return [...entries].sort((left, right) => {
    const leftOrder = viewParamOrder(left[1]);
    const rightOrder = viewParamOrder(right[1]);
    if (leftOrder !== null || rightOrder !== null) {
      const normalizedLeft = leftOrder ?? Number.MAX_SAFE_INTEGER;
      const normalizedRight = rightOrder ?? Number.MAX_SAFE_INTEGER;
      if (normalizedLeft !== normalizedRight) {
        return normalizedLeft - normalizedRight;
      }
    }

    const depthDiff = depthFor(left[0]) - depthFor(right[0]);
    if (depthDiff !== 0) {
      return depthDiff;
    }
    return (indexByName.get(left[0]) ?? 0) - (indexByName.get(right[0]) ?? 0);
  });
};

const isFilledParamValue = (value: ParamValue | undefined): boolean => value !== undefined && value !== null && value !== '';

const clearDependentParamValues = (
  view: TopologyV2ViewCatalogItem | null | undefined,
  params: Record<string, ParamValue>,
  changedParamNames: string[]
): Record<string, ParamValue> => {
  if (!view || changedParamNames.length === 0) {
    return params;
  }

  const changed = new Set(changedParamNames.map((name) => name.trim()).filter(Boolean));
  if (changed.size === 0) {
    return params;
  }

  const next = { ...params };
  let updated = false;
  let discovered = true;

  while (discovered) {
    discovered = false;
    Object.entries(view.params ?? {}).forEach(([name, spec]) => {
      if (changed.has(name)) {
        return;
      }
      const dependencies = optionParamDependencies(name, spec);
      if (!dependencies.some((dependency) => changed.has(dependency))) {
        return;
      }
      changed.add(name);
      discovered = true;
      if (name in next) {
        delete next[name];
        updated = true;
      }
    });
  }

  return updated ? next : params;
};

const nextParamValuesAfterChange = (
  view: TopologyV2ViewCatalogItem | null | undefined,
  params: Record<string, ParamValue>,
  name: string,
  value: ParamValue
): Record<string, ParamValue> => {
  const next = { ...params };
  if (typeof value === 'string' && value === '') {
    delete next[name];
  } else {
    next[name] = value;
  }
  return clearDependentParamValues(view, next, [name]);
};

const optionParamSignature = (
  viewId: string,
  scopeType: string,
  scopeId: string,
  dependencies: string[],
  params: Record<string, ParamValue>,
  extraKey?: unknown
): string =>
  JSON.stringify({
    viewId,
    scopeType,
    scopeId,
    dependencies: dependencies.map((name) => [name, isFilledParamValue(params[name]) ? params[name] : null]),
    extraKey: extraKey ?? null
  });

const parseDependentParamStates = (meta: unknown): Record<string, DependentParamState> => {
  const metaRecord = asRecord(meta);
  const paramState = asRecord(metaRecord?.paramState);
  if (!paramState) {
    return {};
  }

  const out: Record<string, DependentParamState> = {};
  Object.entries(paramState).forEach(([paramName, rawState]) => {
    const state = asRecord(rawState);
    if (!state) {
      return;
    }
    const parent = typeof state.parent === 'string' ? state.parent.trim() : '';
    if (!parent) {
      return;
    }

    const itemsByParentValue: Record<string, TopologyV2ParamOptionItem[]> = {};
    const rawItemsByParentValue = asRecord(state.itemsByParentValue);
    Object.entries(rawItemsByParentValue ?? {}).forEach(([parentValue, rawItems]) => {
      if (!Array.isArray(rawItems)) {
        return;
      }
      itemsByParentValue[parentValue] = rawItems
        .map((item) => {
          const record = asRecord(item);
          const value = typeof record?.value === 'string' ? record.value : '';
          if (!value) {
            return null;
          }
          const label = typeof record?.label === 'string' ? record.label : value;
          return { value, label };
        })
        .filter((item): item is TopologyV2ParamOptionItem => item !== null);
    });

    const supportsByParentValue: Record<string, boolean> = {};
    const rawSupportsByParentValue = asRecord(state.supportsByParentValue);
    Object.entries(rawSupportsByParentValue ?? {}).forEach(([parentValue, rawSupported]) => {
      supportsByParentValue[parentValue] = Boolean(rawSupported);
    });

    out[paramName] = {
      parent,
      itemsByParentValue,
      supportsByParentValue,
      asOf: typeof state.asOf === 'string' ? state.asOf : null
    };
  });
  return out;
};

const metaAsOf = (meta: unknown): string | null => {
  const record = asRecord(meta);
  const asOf = typeof record?.asOf === 'string' ? record.asOf.trim() : '';
  return asOf || null;
};

const recoverInvalidParamCombination = (
  view: TopologyV2ViewCatalogItem | null | undefined,
  params: Record<string, ParamValue>,
  optionsByParam: Record<string, ParamOptionsState>
): Record<string, ParamValue> | null => {
  if (!view) {
    return null;
  }

  const orderedParams = sortVisibleViewParams(view, visibleViewParams(view));
  if (orderedParams.length === 0) {
    return null;
  }

  const nextParams: Record<string, ParamValue> = { ...params };
  let changed = false;

  [...orderedParams].reverse().forEach(([name, spec]) => {
    const current = nextParams[name];
    if (!isFilledParamValue(current)) {
      return;
    }

    const dependencies = optionParamDependencies(name, spec);
    if (dependencies.some((dependency) => !isFilledParamValue(nextParams[dependency]))) {
      delete nextParams[name];
      changed = true;
      return;
    }

    if (!spec.options) {
      return;
    }

    const state = optionsByParam[name];
    if (!state || state.loading || state.error) {
      return;
    }

    const currentValue = String(current).trim();
    const valid = state.items.some((item) => item.value === currentValue);
    if (!valid) {
      delete nextParams[name];
      changed = true;
    }
  });

  if (changed) {
    return nextParams;
  }

  const fallback = [...orderedParams].reverse().find(([name]) => isFilledParamValue(nextParams[name]));
  if (!fallback) {
    return null;
  }
  delete nextParams[fallback[0]];
  return nextParams;
};

const buildEffectiveParamOptions = (
  view: TopologyV2ViewCatalogItem | null | undefined,
  params: Record<string, ParamValue>,
  optionsByParam: Record<string, ParamOptionsState>,
  dependentStates: Record<string, DependentParamState>
): Record<string, ParamOptionsState> => {
  const next = { ...optionsByParam };
  if (!view) {
    return next;
  }

  Object.entries(view.params ?? {}).forEach(([name, spec]) => {
    if (!isDependentParamStateParam(spec)) {
      return;
    }
    const dependentState = dependentStates[name];
    const dependencies = optionParamDependencies(name, spec);
    const parentName = dependentState?.parent || dependencies[0] || '';
    const parentSpec = parentName ? view.params?.[parentName] : undefined;
    const parentLabel = parentSpec ? viewParamLabel(parentName, parentSpec) : 'required filter';
    const parentState = parentName ? optionsByParam[parentName] : undefined;
    const parentValue = parentName && isFilledParamValue(params[parentName]) ? String(params[parentName]).trim() : '';

    if (!parentValue) {
      next[name] = {
        items: [],
        loading: false,
        error: null,
        blocked: true,
        emptyLabel: `Select ${parentLabel.toLowerCase()} first`
      };
      return;
    }

    if (!dependentState) {
      next[name] = {
        items: [],
        loading: parentState?.loading ?? false,
        error: parentState?.error ?? null,
        blocked: false,
        emptyLabel: parentState?.loading
          ? 'Loading options…'
          : parentState?.error
            ? 'Failed to load options'
            : 'No options available'
      };
      return;
    }

    const items = dependentState.itemsByParentValue[parentValue] ?? [];
    const supported = dependentState.supportsByParentValue[parentValue] ?? items.length > 0;
    next[name] = {
      items,
      loading: false,
      error: null,
      blocked: false,
      emptyLabel: !supported || items.length === 0 ? 'No options available' : null
    };
  });

  return next;
};

const readResponseErrorCode = async (error: unknown): Promise<string | null> => {
  if (!(error instanceof ResponseError)) {
    return null;
  }
  try {
    const payload = (await error.response.clone().json()) as Record<string, unknown>;
    return typeof payload.error === 'string' ? payload.error.trim() : null;
  } catch {
    return null;
  }
};

const effectiveScopeTypeForView = (currentScopeType: string, availableScopes: string[]): string => {
  if (availableScopes.length === 0 || availableScopes.includes(currentScopeType)) {
    return currentScopeType;
  }
  return availableScopes[0] ?? currentScopeType;
};

const effectiveScopeIdForResolve = (
  scopeType: string,
  orgId: string | null | undefined,
  selectedSiteId: string,
  scopeIdInput: string
): string => {
  if (scopeType === 'site') {
    return selectedSiteId;
  }
  if (scopeType === 'org') {
    return orgId ?? '';
  }
  return scopeIdInput;
};

const boundScopeUnavailableMessage = (scopeType: string, scopeId: string): string =>
  `This saved view is bound to ${formatScopeLabel(scopeType).toLowerCase()} ${scopeId}, which is not currently available.`;

const toDrilldowns = (value: unknown): TopologyV2Drilldown[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  const items: TopologyV2Drilldown[] = [];
  for (const item of value) {
    const rec = asRecord(item);
    if (!rec) {
      continue;
    }
    const name = typeof rec.name === 'string' ? rec.name.trim() : '';
    const viewId = typeof rec.viewId === 'string' ? rec.viewId.trim() : '';
    if (!name || !viewId) {
      continue;
    }
    const drilldown: TopologyV2Drilldown = { name, viewId };
    const appliesWhen = asRecord(rec.appliesWhen);
    const mode = rec.mode === 'overlay' || rec.mode === 'replace' ? rec.mode : undefined;
    const staticPatch = asRecord(rec._static);
    const dynamicPatch = asRecord(rec.dynamic);
    if (appliesWhen) {
      drilldown.appliesWhen = appliesWhen;
    }
    if (mode) {
      drilldown.mode = mode;
    }
    if (staticPatch) {
      drilldown._static = staticPatch as TopologyV2ResolveRequestPatch;
    }
    if (dynamicPatch) {
      drilldown.dynamic = dynamicPatch as TopologyV2ResolveRequestPatch;
    }
    items.push(drilldown);
  }
  return items;
};

export type ProviderEdgePortSignalsRecord = {
  hasPublicIp?: boolean | null;
  publicIpAddresses?: string[] | null;
  bgpPeerCount?: number | null;
  ebgpPeerCount?: number | null;
  remoteAsns?: string[] | null;
  hasVpnConfig?: boolean | null;
  vpnSessionCount?: number | null;
  transportSessionCount?: number | null;
  lldpNeighborCount?: number | null;
  cdpNeighborCount?: number | null;
  learnedMacCount?: number | null;
  arpEntryCount?: number | null;
  defaultRouteMatch?: boolean | null;
  hasNatOutside?: boolean | null;
  negativeAccessSignal?: boolean | null;
  isShutdown?: boolean | null;
};

export type ProviderEdgePortRecord = {
  deviceName?: string | null;
  interfaceName?: string | null;
  classification?: string | null;
  confidence?: number | null;
  reasons?: string[] | null;
  signals?: ProviderEdgePortSignalsRecord | null;
};

export const normalizeProviderEdgePortRecord = (value: unknown): ProviderEdgePortRecord | null => {
  const record = asRecord(value);
  if (!record) {
    return null;
  }
  const signals = asRecord(record.signals);
  return {
    deviceName: getString(record.deviceName ?? record.device_name) ?? null,
    interfaceName: getString(record.interfaceName ?? record.interface_name) ?? null,
    classification: getString(record.classification) ?? null,
    confidence: getNumber(record.confidence) ?? null,
    reasons: Array.isArray(record.reasons) ? record.reasons.map((item) => getString(item)).filter(Boolean) as string[] : [],
    signals: signals
      ? {
          hasPublicIp: getOptionalBoolean(signals.hasPublicIp ?? signals.has_public_ip),
          publicIpAddresses: getStringArray(signals.publicIpAddresses ?? signals.public_ip_addresses) ?? [],
          bgpPeerCount: getNumber(signals.bgpPeerCount ?? signals.bgp_peer_count) ?? null,
          ebgpPeerCount: getNumber(signals.ebgpPeerCount ?? signals.ebgp_peer_count) ?? null,
          remoteAsns: getStringArray(signals.remoteAsns ?? signals.remote_asns) ?? [],
          hasVpnConfig: getOptionalBoolean(signals.hasVpnConfig ?? signals.has_vpn_config),
          vpnSessionCount: getNumber(signals.vpnSessionCount ?? signals.vpn_session_count) ?? null,
          transportSessionCount: getNumber(signals.transportSessionCount ?? signals.transport_session_count) ?? null,
          lldpNeighborCount: getNumber(signals.lldpNeighborCount ?? signals.lldp_neighbor_count) ?? null,
          cdpNeighborCount: getNumber(signals.cdpNeighborCount ?? signals.cdp_neighbor_count) ?? null,
          learnedMacCount: getNumber(signals.learnedMacCount ?? signals.learned_mac_count) ?? null,
          arpEntryCount: getNumber(signals.arpEntryCount ?? signals.arp_entry_count) ?? null,
          defaultRouteMatch: getOptionalBoolean(signals.defaultRouteMatch ?? signals.default_route_match),
          hasNatOutside: getOptionalBoolean(signals.hasNatOutside ?? signals.has_nat_outside),
          negativeAccessSignal: getOptionalBoolean(signals.negativeAccessSignal ?? signals.negative_access_signal),
          isShutdown: getOptionalBoolean(signals.isShutdown ?? signals.is_shutdown)
        }
      : null
  };
};

export const normalizeProviderEdgePorts = (value: unknown): ProviderEdgePortRecord[] =>
  Array.isArray(value) ? (value.map(normalizeProviderEdgePortRecord).filter(Boolean) as ProviderEdgePortRecord[]) : [];

export const providerEdgeClassificationTone = (classification?: string | null): 'success' | 'warning' | 'ghost' => {
  const normalized = (classification ?? '').trim().toLowerCase();
  if (normalized.startsWith('confirmed') || normalized.startsWith('probable')) {
    return 'success';
  }
  if (normalized.includes('not_provider') || normalized.includes('insufficient')) {
    return 'ghost';
  }
  return normalized ? 'warning' : 'ghost';
};

const formatProviderEdgeBadgeClass = (tone: 'success' | 'warning' | 'ghost') => `ui-badge ui-badge--${tone}`;

export const formatProviderEdgeLabel = (classification?: string | null): string => {
  const normalized = (classification ?? '').trim();
  if (!normalized) {
    return 'Unclassified';
  }
  return normalized
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

export const formatProviderEdgeConfidence = (confidence?: number | null): string => {
  return formatConfidencePercent(confidence);
};

export const describeProviderEdgeEvidence = (port?: ProviderEdgePortRecord | null): string => {
  if (!port) {
    return '—';
  }
  const evidence: string[] = [];
  if (port.signals?.hasPublicIp && (port.signals.publicIpAddresses?.length ?? 0) > 0) {
    evidence.push(`Public IP ${port.signals?.publicIpAddresses?.join(', ')}`);
  } else if (port.signals?.hasPublicIp) {
    evidence.push('Public IP');
  }
  if ((port.signals?.ebgpPeerCount ?? 0) > 0) {
    evidence.push(`${port.signals?.ebgpPeerCount} eBGP peer${port.signals?.ebgpPeerCount === 1 ? '' : 's'}`);
  } else if ((port.signals?.bgpPeerCount ?? 0) > 0) {
    evidence.push(`${port.signals?.bgpPeerCount} BGP peer${port.signals?.bgpPeerCount === 1 ? '' : 's'}`);
  }
  if ((port.signals?.transportSessionCount ?? 0) > 0) {
    evidence.push(`${port.signals?.transportSessionCount} transport session${port.signals?.transportSessionCount === 1 ? '' : 's'}`);
  }
  if ((port.signals?.vpnSessionCount ?? 0) > 0) {
    evidence.push(`${port.signals?.vpnSessionCount} VPN session${port.signals?.vpnSessionCount === 1 ? '' : 's'}`);
  }
  if (port.signals?.defaultRouteMatch) {
    evidence.push('Default route');
  }
  if (port.signals?.hasNatOutside) {
    evidence.push('NAT outside');
  }
  if ((port.signals?.lldpNeighborCount ?? 0) > 0 || (port.signals?.cdpNeighborCount ?? 0) > 0) {
    const count = (port.signals?.lldpNeighborCount ?? 0) + (port.signals?.cdpNeighborCount ?? 0);
    evidence.push(`${count} discovery neighbor${count === 1 ? '' : 's'}`);
  }
  if ((port.signals?.learnedMacCount ?? 0) > 0) {
    evidence.push(`${port.signals?.learnedMacCount} learned MAC${port.signals?.learnedMacCount === 1 ? '' : 's'}`);
  }
  if ((port.signals?.arpEntryCount ?? 0) > 0) {
    evidence.push(`${port.signals?.arpEntryCount} ARP entr${port.signals?.arpEntryCount === 1 ? 'y' : 'ies'}`);
  }
  if (evidence.length > 0) {
    return evidence.join(' · ');
  }
  if ((port.reasons?.length ?? 0) > 0) {
    return port.reasons?.join(' · ') ?? '—';
  }
  return '—';
};

export const ProviderEdgePortTable = ({
  ports,
  showDevice = false
}: {
  ports: ProviderEdgePortRecord[];
  showDevice?: boolean;
}) => (
  <div className="topology-v2__provider-edge-box">
    <div
      className={[
        'topology-v2__provider-edge-header',
        showDevice ? 'topology-v2__provider-edge-header--device' : ''
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {showDevice ? <span>Device</span> : null}
      <span>Interface</span>
      <span>Classification</span>
      <span>Confidence</span>
      <span>Evidence</span>
    </div>
    {ports.map((port, index) => (
      <div
        key={`${port.deviceName ?? 'device'}-${port.interfaceName ?? 'iface'}-${index}`}
        className={[
          'topology-v2__provider-edge-row',
          showDevice ? 'topology-v2__provider-edge-row--device' : ''
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {showDevice ? (
          <div className="topology-v2__provider-edge-device">
            <strong>{port.deviceName || '—'}</strong>
          </div>
        ) : null}
        <div className="topology-v2__provider-edge-interface">
          <strong>{port.interfaceName || '—'}</strong>
        </div>
        <div className="topology-v2__provider-edge-classification">
          <span className={formatProviderEdgeBadgeClass(providerEdgeClassificationTone(port.classification))}>
            {formatProviderEdgeLabel(port.classification)}
          </span>
        </div>
        <div className="topology-v2__provider-edge-confidence">
          <span className={getConfidenceBadgeClass(port.confidence)}>{formatProviderEdgeConfidence(port.confidence)}</span>
        </div>
        <div className="topology-v2__provider-edge-evidence">
          <span>{describeProviderEdgeEvidence(port)}</span>
        </div>
      </div>
    ))}
  </div>
);

type TopologyV2PageProps = {
  embedded?: boolean;
  initialSiteId?: string;
  forceViewId?: string;
  hideControls?: boolean;
};

const TopologyV2Page = ({
  embedded = false,
  initialSiteId,
  forceViewId,
  hideControls = false
}: TopologyV2PageProps = {}) => {
  const orgId = useActiveOrgId();
  const orgDisplayName = useActiveOrgDisplayName();
  const { apiBaseUrl, mockRoutes } = useConfig();
  const [searchParams, setSearchParams] = useSearchParams();
  const basePath = resolveBasePath(apiBaseUrl);
  const { fetchOrgResource, fetchWithAuthRetry } = useAuthFetch();
  const topologyApi = useTopologyApi();
  const visibilityApi = useVisibilityApi();

  const [sites, setSites] = useState<Site[]>([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [sitesError, setSitesError] = useState<string | null>(null);
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');

  const [views, setViews] = useState<TopologyV2ViewCatalogItem[]>([]);
  const [viewsLoading, setViewsLoading] = useState(false);
  const [viewsError, setViewsError] = useState<string | null>(null);
  const [selectedViewId, setSelectedViewId] = useState<string>('');
  const [savedViewName, setSavedViewName] = useState('');
  const [savedViewDescription, setSavedViewDescription] = useState('');
  const [savedViewMode, setSavedViewMode] = useState<TopologyV2ViewScopeModeschema>('reusable');
  const [savedViewBusyAction, setSavedViewBusyAction] = useState<string | null>(null);
  const [savedViewError, setSavedViewError] = useState<string | null>(null);
  const [savedViewResult, setSavedViewResult] = useState<TopologyV2ViewSaveResponse | null>(null);
  const [deleteViewConfirmationOpen, setDeleteViewConfirmationOpen] = useState(false);
  const [deleteToast, setDeleteToast] = useState<{ tone: StatusToastTone; message: string } | null>(null);
  const [saveDrawerOpen, setSaveDrawerOpen] = useState(false);
  const [layersDrawerOpen, setLayersDrawerOpen] = useState(false);
  const [workspaceMode, setWorkspaceMode] = useState<'split' | 'topology' | 'chat'>('split');
  const [chatPaneWidth, setChatPaneWidth] = useState(DEFAULT_CHAT_PANE_WIDTH);
  const [isResizingWorkspace, setIsResizingWorkspace] = useState(false);

  const [viewParams, setViewParams] = useState<Record<string, ParamValue>>({});
  const [paramError, setParamError] = useState<string | null>(null);
  const [paramOptions, setParamOptions] = useState<Record<string, ParamOptionsState>>({});
  const [dependentParamStates, setDependentParamStates] = useState<Record<string, DependentParamState>>({});
  const [dependentParamRefreshNonce, setDependentParamRefreshNonce] = useState(0);
  const [autoResolvePending, setAutoResolvePending] = useState(false);
  const lastResolvedKeyRef = useRef<string | null>(null);
  const paramOptionSignatureRef = useRef<Record<string, string>>({});
  const paramOptionLoadedSignatureRef = useRef<Record<string, string>>({});
  const paramOptionRequestedSignatureRef = useRef<Record<string, string>>({});
  const resolveGenerationRef = useRef(0);
  const focusedResolveGenerationRef = useRef(0);
  const streamAbortRef = useRef<AbortController | null>(null);
  const lastSeqRef = useRef<number>(0);
  const baseLastSeqRef = useRef<number>(0);
  const focusedLastSeqRef = useRef<number>(0);

  const [resolved, setResolved] = useState<TopologyV2ResolveResponse | null>(null);
  const [baseResolveRequest, setBaseResolveRequest] = useState<Record<string, unknown> | null>(null);
  const [focusedResolved, setFocusedResolved] = useState<TopologyV2ResolveResponse | null>(null);
  const [focusedResolveRequest, setFocusedResolveRequest] = useState<Record<string, unknown> | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [resolveLoading, setResolveLoading] = useState(false);
  const [resolveLimits, setResolveLimits] = useState<Record<string, unknown>>({});
  const [overlays, setOverlays] = useState<
    Record<
      string,
      {
        viewId: string;
        request: Record<string, unknown>;
        response: TopologyV2ResolveResponse;
        ownerKey: string;
        hiddenEdgeIds: string[];
      }
    >
  >({});

  type SelectedElement = TopologyWorkbenchSelectedElement;
  const [selectedElements, setSelectedElements] = useState<SelectedElement[]>([]);
  const [workbenchSession, setWorkbenchSession] = useState<VisibilitySession | null>(null);
  const [workbenchProposal, setWorkbenchProposal] = useState<VisibilityProposal | null>(null);
  const [workbenchThreadId, setWorkbenchThreadId] = useState<string | null>(null);
  const [workbenchOverlayState, setWorkbenchOverlayState] = useState<TopologyOverlayRuntimeState>(
    emptyTopologyOverlayState()
  );
  const [savedOverlayState, setSavedOverlayState] = useState<TopologyOverlayRuntimeState>(emptyTopologyOverlayState());
  const [workbenchRuntimeGraph, setWorkbenchRuntimeGraph] = useState<TopologyV2Graph | null>(null);
  const [workbenchRuntimeGraphSettled, setWorkbenchRuntimeGraphSettled] = useState(false);
  const [workbenchError, setWorkbenchError] = useState<string | null>(null);
  const [workbenchBusyAction, setWorkbenchBusyAction] = useState<string | null>(null);
  const [workbenchRestoring, setWorkbenchRestoring] = useState(false);
  const [pendingWorkbenchSwitch, setPendingWorkbenchSwitch] = useState<WorkbenchSwitchRequest | null>(null);
  const workbenchStreamAbortRef = useRef<AbortController | null>(null);
  const workbenchVisibleGraphRef = useRef<TopologyV2Graph | null>(null);
  const workbenchSessionRef = useRef<VisibilitySession | null>(null);
  const workbenchOverlayStateRef = useRef<TopologyOverlayRuntimeState>(emptyTopologyOverlayState());
  const preserveWorkbenchRuntimeGraphRef = useRef(false);
  const workspaceRef = useRef<HTMLDivElement | null>(null);
  const workspaceResizeRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const mockTopologyDemoDefaultsAppliedRef = useRef(false);
  const mockTopologySessionBootstrappedRef = useRef<string | null>(null);

  const [scopeType, setScopeType] = useState<string>('site');
  const [scopeIdInput, setScopeIdInput] = useState<string>('');
  const [layoutResetCounter, setLayoutResetCounter] = useState(0);
  const routeViewId = (searchParams.get('viewId') ?? '').trim();
  const routeScopeType = (searchParams.get('scopeType') ?? '').trim();
  const routeScopeId = (searchParams.get('scopeId') ?? '').trim();
  const routeSiteId = (searchParams.get('siteId') ?? '').trim();
  const routeAsOfParam = (searchParams.get('asOf') ?? '').trim();
  const routeAsOf = toIsoString(routeAsOfParam) ?? '';
  const requestedViewId = (forceViewId ?? routeViewId).trim();
  const requestedScopeType = routeScopeType || (routeSiteId ? 'site' : '');
  const requestedScopeId = routeScopeId || routeSiteId;
  const requestedInitialSiteId = (initialSiteId ?? (requestedScopeType === 'site' ? requestedScopeId : '')).trim();
  const mockTopologyAttachmentDemoEnabled = !embedded && Array.isArray(mockRoutes) && mockRoutes.includes('org-console');
  const [timeMode, setTimeMode] = useState<TimeMode>(() => (routeAsOf ? 'fixed' : 'live'));
  const [asOfInput, setAsOfInput] = useState(() => formatDateTimeLocalInput(routeAsOf));
  const [asOfValue, setAsOfValue] = useState<string | null>(() => (routeAsOf ? routeAsOf : null));
  const requestedAsOf = useMemo(() => (timeMode === 'fixed' ? asOfValue : null), [asOfValue, timeMode]);
  const skipRouteAsOfHydrationRef = useRef(false);
  const pendingLocalAsOfSyncRef = useRef(false);

  useEffect(() => {
    if (!pendingLocalAsOfSyncRef.current && routeAsOf !== (requestedAsOf ?? '')) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    if (requestedAsOf) {
      next.set('asOf', requestedAsOf);
    } else {
      next.delete('asOf');
    }
    if (next.toString() !== searchParams.toString()) {
      skipRouteAsOfHydrationRef.current = true;
      setSearchParams(next, { replace: true });
      return;
    }
    pendingLocalAsOfSyncRef.current = false;
  }, [requestedAsOf, routeAsOf, searchParams, setSearchParams]);

  const requestOrgApi = useCallback(
    async (resource: string, init: RequestInit = {}) => {
      if (!orgId) {
        throw new Error('Organization is required.');
      }
      const url = buildOrgUrl(basePath, orgId, resource);
      const headers = new Headers(init.headers ?? undefined);
      const isFormData = typeof FormData !== 'undefined' && init.body instanceof FormData;
      if (!headers.has('Accept')) {
        headers.set('Accept', resource.includes('/stream') ? 'text/event-stream' : 'application/json');
      }
      if (init.body && !isFormData && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
      }
      const response = await fetchWithAuthRetry(url, {
        ...init,
        headers
      });
      if (!response.ok) {
        throw new ApiResponseError(response, `Request failed with status ${response.status}`);
      }
      return response;
    },
    [basePath, fetchWithAuthRetry, orgId]
  );

  const requestTopologyAction = useCallback(
    async (href: string, init: RequestInit = {}) => {
      const raw = href.trim();
      if (!raw) {
        throw new Error('Missing action URL.');
      }
      if (!orgId) {
        throw new Error('Organization is required.');
      }
      if (raw.startsWith('//') || TOPOLOGY_ACTION_ABSOLUTE_URL_PATTERN.test(raw)) {
        throw new Error('Topology action URL must be relative.');
      }
      const normalizedPath = raw.startsWith('/') ? raw : `/${raw}`;
      const activeOrgPrefix = `/orgs/${encodeURIComponent(orgId)}/`;
      if (!normalizedPath.startsWith(activeOrgPrefix)) {
        throw new Error('Topology action URL must target the active organization.');
      }
      const target = `${basePath}${normalizedPath}`;
      const headers = new Headers(init.headers ?? undefined);
      const isFormData = typeof FormData !== 'undefined' && init.body instanceof FormData;
      if (!headers.has('Accept')) {
        headers.set('Accept', 'application/json');
      }
      if (init.body && !isFormData && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
      }
      const response = await fetchWithAuthRetry(target, {
        ...init,
        headers
      });
      if (!response.ok) {
        throw new ApiResponseError(response, `Request failed with status ${response.status}`);
      }
      return response;
    },
    [basePath, fetchWithAuthRetry, orgId]
  );

  const loadViews = useCallback(async (): Promise<TopologyV2ViewCatalogItem[]> => {
    if (!orgId) {
      return [];
    }
    const response = await visibilityApi.orgsOrgIdTopologyViewsGet({
      orgId,
      origin: 'all'
    });
    return response.items ?? [];
  }, [orgId, visibilityApi]);

  const activeView = useMemo(
    () => views.find((view) => view.viewId === selectedViewId) ?? null,
    [selectedViewId, views]
  );
  const currentScopeBoundScope = useMemo(() => getTopologyViewBoundScope(activeView), [activeView]);
  const activeViewScopeTypes = useMemo(() => scopeTypesForView(activeView), [activeView]);
  const resolveScopeType = useMemo(
    () => effectiveScopeTypeForView(scopeType, activeViewScopeTypes),
    [activeViewScopeTypes, scopeType]
  );

  const sortedSites = useMemo(
    () => [...sites].sort((a, b) => (a.name ?? a.id ?? '').localeCompare(b.name ?? b.id ?? '')),
    [sites]
  );
  const boundScopeError = useMemo(() => {
    if (!currentScopeBoundScope) {
      return null;
    }
    if (
      activeViewScopeTypes.length > 0 &&
      !activeViewScopeTypes.includes(currentScopeBoundScope.scopeType)
    ) {
      return `This saved view is bound to ${formatScopeLabel(currentScopeBoundScope.scopeType).toLowerCase()} ${currentScopeBoundScope.scopeId}, which is not supported by this view.`;
    }
    if (currentScopeBoundScope.scopeType !== 'site' || sitesLoading) {
      return null;
    }
    const hasSite = sortedSites.some((site) => (site.id ?? '').trim() === currentScopeBoundScope.scopeId);
    return hasSite ? null : boundScopeUnavailableMessage(currentScopeBoundScope.scopeType, currentScopeBoundScope.scopeId);
  }, [activeViewScopeTypes, currentScopeBoundScope, sitesLoading, sortedSites]);
  const effectiveViewScopeType = currentScopeBoundScope
    ? effectiveScopeTypeForView(currentScopeBoundScope.scopeType, activeViewScopeTypes)
    : resolveScopeType;
  const effectiveViewScopeId = boundScopeError
    ? ''
    : currentScopeBoundScope
      ? currentScopeBoundScope.scopeId
      : effectiveScopeIdForResolve(resolveScopeType, orgId, selectedSiteId, scopeIdInput);

  const sortedViews = useMemo(
    () => [...views].sort((a, b) => (a.name ?? a.viewId ?? '').localeCompare(b.name ?? b.viewId ?? '')),
    [views]
  );
  const groupedViews = useMemo(() => partitionTopologyViews(sortedViews), [sortedViews]);
  const visibleParams = useMemo(() => sortVisibleViewParams(activeView, visibleViewParams(activeView)), [activeView]);

  const clampChatPaneWidth = useCallback((nextWidth: number) => {
    const containerWidth = workspaceRef.current?.clientWidth ?? 0;
    const maxWidth = Math.max(MIN_CHAT_PANE_WIDTH, containerWidth - MIN_TOPOLOGY_PANE_WIDTH);
    return Math.min(Math.max(nextWidth, MIN_CHAT_PANE_WIDTH), maxWidth);
  }, []);

  const resetWorkspaceLayout = useCallback(() => {
    setWorkspaceMode('split');
    setChatPaneWidth(DEFAULT_CHAT_PANE_WIDTH);
  }, []);

  const applyAsOfSelection = useCallback((nextAsOf: string | null | undefined, syncToRoute = true) => {
    if (syncToRoute) {
      pendingLocalAsOfSyncRef.current = true;
    }
    const normalized = typeof nextAsOf === 'string' ? nextAsOf.trim() : '';
    if (normalized) {
      setTimeMode('fixed');
      setAsOfValue(normalized);
      setAsOfInput(formatDateTimeLocalInput(normalized));
      return;
    }
    setTimeMode('live');
    setAsOfValue(null);
    setAsOfInput('');
  }, []);

  useEffect(() => {
    if (skipRouteAsOfHydrationRef.current) {
      skipRouteAsOfHydrationRef.current = false;
      return;
    }
    const normalizedRouteAsOf = routeAsOf || null;
    if (normalizedRouteAsOf) {
      if (timeMode === 'fixed' && asOfValue === normalizedRouteAsOf) {
        return;
      }
      applyAsOfSelection(normalizedRouteAsOf, false);
      return;
    }
    if (timeMode === 'fixed' && asOfValue === null) {
      return;
    }
    if (timeMode === 'live' && asOfValue === null && asOfInput === '') {
      return;
    }
    applyAsOfSelection(null, false);
  }, [applyAsOfSelection, asOfInput, asOfValue, routeAsOf, timeMode]);

  const handleWorkspaceResizeStart = useCallback(
    (event: ReactMouseEvent<HTMLElement>) => {
      if (workspaceMode !== 'split') {
        setWorkspaceMode('split');
      }
      workspaceResizeRef.current = {
        startX: event.clientX,
        startWidth: chatPaneWidth
      };
      setIsResizingWorkspace(true);
      event.preventDefault();
    },
    [chatPaneWidth, workspaceMode]
  );

  useEffect(() => {
    if (!isResizingWorkspace) {
      return;
    }

    const handleMouseMove = (event: MouseEvent) => {
      const session = workspaceResizeRef.current;
      if (!session) {
        return;
      }
      const delta = event.clientX - session.startX;
      setChatPaneWidth(clampChatPaneWidth(session.startWidth - delta));
    };

    const handleMouseUp = () => {
      workspaceResizeRef.current = null;
      setIsResizingWorkspace(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('blur', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('blur', handleMouseUp);
    };
  }, [clampChatPaneWidth, isResizingWorkspace]);

  useEffect(() => {
    setChatPaneWidth((current) => clampChatPaneWidth(current));
  }, [clampChatPaneWidth]);

  const activeResolved = focusedResolved ?? resolved;
  const activeResolveRequest = focusedResolved && focusedResolveRequest ? focusedResolveRequest : baseResolveRequest;
  const baseLegend = useMemo(() => ((activeResolved?.legend ?? {}) as TopologyV2Legend), [activeResolved?.legend]);
  const baseDrilldowns = useMemo(() => toDrilldowns(activeResolved?.drilldowns), [activeResolved?.drilldowns]);
  const baseContextMeta = useMemo(
    () => ((activeResolved?.meta?.context ?? {}) as Record<string, unknown>),
    [activeResolved?.meta?.context]
  );
  const workbenchBase = useMemo(() => {
    if (!selectedViewId || !activeResolveRequest) {
      return null;
    }
    try {
      return buildTopologyWorkbenchBase(selectedViewId, activeResolveRequest);
    } catch {
      return null;
    }
  }, [activeResolveRequest, selectedViewId]);
  const workbenchResumeKey = useMemo(
    () => (workbenchBase ? buildTopologyWorkbenchResumeKey(workbenchBase) : null),
    [workbenchBase]
  );

  const merged = useMemo(() => {
    if (!activeResolved?.graph) {
      return null;
    }

    const baseGraph = activeResolved.graph;
    const nodeById = new Map<string, TopologyV2Node>();
    const edgeById = new Map<string, TopologyV2Edge>();

    const overlayKeyByNodeId: Record<string, string | undefined> = {};
    const overlayKeyByEdgeId: Record<string, string | undefined> = {};

    const edgeGroupIndex: Record<string, Record<string, string[]>> = {};
    const edgeGroupKey = (edge: TopologyV2Edge): string => {
      const key = edge.properties?.overlayKey;
      return typeof key === 'string' && key.trim() ? key.trim() : edge.id;
    };
    const addEdgeGroup = (ownerKey: string, edge: TopologyV2Edge) => {
      const groupKey = edgeGroupKey(edge);
      const byOwner = edgeGroupIndex[ownerKey] ?? {};
      const list = byOwner[groupKey] ?? [];
      list.push(edge.id);
      byOwner[groupKey] = list;
      edgeGroupIndex[ownerKey] = byOwner;
    };

    (baseGraph.nodes ?? []).forEach((n) => nodeById.set(n.id, n));
    (baseGraph.edges ?? []).forEach((e) => {
      edgeById.set(e.id, e);
      addEdgeGroup('base', e);
    });

    const overlayKeys = Object.keys(overlays);
    const hiddenEdgeIds = new Set<string>();
    overlayKeys.forEach((key) => {
      (overlays[key]?.hiddenEdgeIds ?? []).forEach((edgeId) => hiddenEdgeIds.add(edgeId));
    });

    const nodes: TopologyV2Node[] = [...(baseGraph.nodes ?? [])];
    const edges: TopologyV2Edge[] = (baseGraph.edges ?? []).filter((e) => !hiddenEdgeIds.has(e.id));

    overlayKeys.forEach((overlayKey) => {
      const overlay = overlays[overlayKey];
      const graph = overlay?.response?.graph;
      if (!graph) {
        return;
      }
      (graph.nodes ?? []).forEach((n) => {
        if (nodeById.has(n.id)) {
          return;
        }
        nodeById.set(n.id, n);
        nodes.push(n);
        overlayKeyByNodeId[n.id] = overlayKey;
      });
      (graph.edges ?? []).forEach((e) => {
        addEdgeGroup(overlayKey, e);
        if (hiddenEdgeIds.has(e.id)) {
          return;
        }
        if (edgeById.has(e.id)) {
          return;
        }
        edgeById.set(e.id, e);
        edges.push(e);
        overlayKeyByEdgeId[e.id] = overlayKey;
      });
    });

    const graphId = [baseGraph.graphId, ...overlayKeys.sort()].join('|');

    const drilldownsByOverlayKey: Record<string, TopologyV2Drilldown[]> = {};
    const legendByOverlayKey: Record<string, TopologyV2Legend> = {};
    const contextByOverlayKey: Record<string, Record<string, unknown>> = {};
    const requestByOverlayKey: Record<string, Record<string, unknown>> = {};

    drilldownsByOverlayKey.base = baseDrilldowns;
    legendByOverlayKey.base = baseLegend;
    contextByOverlayKey.base = baseContextMeta;
    if (activeResolveRequest) {
      requestByOverlayKey.base = activeResolveRequest;
    }

    overlayKeys.forEach((overlayKey) => {
      const overlay = overlays[overlayKey];
      if (!overlay) {
        return;
      }
      drilldownsByOverlayKey[overlayKey] = toDrilldowns(overlay.response.drilldowns);
      legendByOverlayKey[overlayKey] = (overlay.response.legend ?? {}) as TopologyV2Legend;
      contextByOverlayKey[overlayKey] = (overlay.response.meta?.context ?? {}) as Record<string, unknown>;
      requestByOverlayKey[overlayKey] = overlay.request;
    });

    return {
      graph: {
        graphId,
        generatedAt: baseGraph.generatedAt,
        scope: baseGraph.scope,
        meta: {
          ...(baseGraph.meta ?? {}),
          ...(Object.keys(baseContextMeta).length > 0 ? { context: baseContextMeta } : {})
        },
        nodes,
        edges
      } as TopologyV2Graph,
      nodeById,
      edgeById,
      overlayKeyByNodeId,
      overlayKeyByEdgeId,
      edgeGroupIndex,
      drilldownsByOverlayKey,
      legendByOverlayKey,
      contextByOverlayKey,
      requestByOverlayKey
    };
  }, [activeResolveRequest, activeResolved?.graph, baseContextMeta, baseDrilldowns, baseLegend, overlays]);

  const topologyGraph = merged?.graph ?? null;
  const visibleTopologyGraph = topologyGraph;
  const savedViewPresentation = useMemo(
    () => (visibleTopologyGraph ? deriveTopologyOverlayPresentation(visibleTopologyGraph, savedOverlayState) : null),
    [savedOverlayState, visibleTopologyGraph]
  );
  const savedViewGraph = savedViewPresentation?.graph ?? null;
  const derivedWorkbenchPresentation = useMemo(
    () => (!embedded && savedViewGraph ? deriveTopologyOverlayPresentation(savedViewGraph, workbenchOverlayState) : null),
    [embedded, savedViewGraph, workbenchOverlayState]
  );
  const derivedWorkbenchGraph = derivedWorkbenchPresentation?.graph ?? null;
  const activeRelatedView = derivedWorkbenchPresentation?.relatedView ?? savedViewPresentation?.relatedView;
  const renderedTopologyGraph = selectTopologyWorkbenchGraph(
    workbenchRuntimeGraph,
    derivedWorkbenchGraph,
    savedViewGraph,
    visibleTopologyGraph,
    workbenchRuntimeGraphSettled
  );
  useEffect(() => {
    if (embedded) {
      preserveWorkbenchRuntimeGraphRef.current = false;
      setWorkbenchRuntimeGraph(null);
      setWorkbenchRuntimeGraphSettled(false);
      return;
    }
    const preserveRuntimeGraph = preserveWorkbenchRuntimeGraphRef.current;
    setWorkbenchRuntimeGraph((current) => {
      if (preserveRuntimeGraph) {
        preserveWorkbenchRuntimeGraphRef.current = false;
        return current;
      }
      return derivedWorkbenchGraph;
    });
    if (!preserveRuntimeGraph) {
      setWorkbenchRuntimeGraphSettled(false);
    }
  }, [derivedWorkbenchGraph, embedded]);

  useEffect(() => {
    workbenchSessionRef.current = workbenchSession;
  }, [workbenchSession]);

  useEffect(() => {
    workbenchOverlayStateRef.current = workbenchOverlayState;
  }, [workbenchOverlayState]);

  useEffect(() => {
    workbenchVisibleGraphRef.current = derivedWorkbenchGraph ?? savedViewGraph ?? visibleTopologyGraph;
  }, [derivedWorkbenchGraph, savedViewGraph, visibleTopologyGraph]);

  useEffect(() => {
    setSavedOverlayState((current) => {
      const next = hydrateResolvedTopologyViewOverlayState(activeResolved);
      if (current.localHiddenLayerIds.length === 0) {
        return next;
      }
      const knownLayerIds = new Set(next.layers.map((layer) => layer.id));
      return {
        ...next,
        localHiddenLayerIds: current.localHiddenLayerIds.filter((layerId) => knownLayerIds.has(layerId))
      };
    });
  }, [activeResolved]);

  const selectedNodeIds = useMemo(() => {
    const set = new Set<string>();
    selectedElements.forEach((item) => {
      if (item.kind === 'node') {
        set.add(item.id);
      }
    });
    if (!embedded) {
      Array.from(workbenchOverlayState.selection.nodeIds ?? []).forEach((nodeId) => set.add(nodeId));
    }
    return set;
  }, [embedded, selectedElements, workbenchOverlayState.selection.nodeIds]);

  const selectedEdgeIds = useMemo(() => {
    const set = new Set<string>();
    selectedElements.forEach((item) => {
      if (item.kind === 'edge') {
        set.add(item.id);
      }
    });
    if (!embedded) {
      Array.from(workbenchOverlayState.selection.edgeIds ?? []).forEach((edgeId) => set.add(edgeId));
    }
    return set;
  }, [embedded, selectedElements, workbenchOverlayState.selection.edgeIds]);
  const clearSelection = useCallback(() => setSelectedElements([]), []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase() ?? '';
      if (tag === 'input' || tag === 'textarea' || tag === 'select') {
        return;
      }
      clearSelection();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [clearSelection]);

  useEffect(() => {
    if (!merged) {
      setSelectedElements([]);
      return;
    }
    setSelectedElements((prev) =>
      prev.filter((item) => (item.kind === 'node' ? merged.nodeById.has(item.id) : merged.edgeById.has(item.id)))
    );
  }, [merged]);

  const syncWorkbenchSession = useCallback(
    (session: VisibilitySession | null, fallbackThreadId: string | null = null) => {
      const nextOverlayState = session
        ? deriveVisibilitySessionOverlayState(session)
        : emptyTopologyOverlayState(workbenchBase ?? undefined);
      const preserveRuntimeGraph = shouldPreserveWorkbenchRuntimeGraph(
        workbenchSessionRef.current,
        workbenchOverlayStateRef.current,
        nextOverlayState,
        session?.pendingProposalId
      );
      preserveWorkbenchRuntimeGraphRef.current = preserveRuntimeGraph;
      setWorkbenchSession(session);
      workbenchSessionRef.current = session;
      setWorkbenchThreadId(session?.chatThreadId ?? fallbackThreadId ?? null);
      setWorkbenchOverlayState(nextOverlayState);
      workbenchOverlayStateRef.current = nextOverlayState;
      setWorkbenchRuntimeGraph((current) => (preserveRuntimeGraph ? current : null));
      if (!preserveRuntimeGraph) {
        setWorkbenchRuntimeGraphSettled(false);
      }
      if (!session?.pendingProposalId) {
        setWorkbenchProposal(null);
      }
    },
    [workbenchBase]
  );

  const refreshWorkbenchSession = useCallback(async () => {
    if (embedded || !orgId || !workbenchSession?.id) {
      return;
    }
    try {
      const session = await topologyApi.getVisibilitySession({
        orgId,
        sessionId: workbenchSession.id
      });
      syncWorkbenchSession(session, workbenchThreadId);
    } catch {
      // Leave the current UI state alone; the next SSE or explicit action can still recover it.
    }
  }, [embedded, orgId, syncWorkbenchSession, topologyApi, workbenchSession?.id, workbenchThreadId]);

  const restoreWorkbenchBindingFromSession = useCallback((session: VisibilitySession) => {
    const base = session.state?.base;
    if (!base) {
      return;
    }
    const draft = buildTopologyWorkbenchBindingDraft(base);
    setSelectedViewId(draft.viewId);
    setScopeType(draft.scopeType);
    if (draft.scopeType === 'site') {
      setSelectedSiteId(draft.scopeId);
      setScopeIdInput('');
    } else if (draft.scopeType === 'org') {
      setScopeIdInput('');
    } else {
      setScopeIdInput(draft.scopeId);
    }
    setViewParams(draft.params);
    setResolveLimits(draft.limits);
    lastResolvedKeyRef.current = null;
    applyAsOfSelection(draft.asOf ?? null);
  }, [applyAsOfSelection]);

  const applyWorkbenchSwitch = useCallback(
    (next: WorkbenchSwitchRequest) => {
      lastResolvedKeyRef.current = null;
      setSelectedViewId(next.viewId);
      setScopeType(next.scopeType);
      if (next.scopeType === 'site') {
        setSelectedSiteId(next.scopeId);
        setScopeIdInput('');
      } else if (next.scopeType === 'org') {
        setScopeIdInput('');
      } else {
        setScopeIdInput(next.scopeId);
      }
      setViewParams(next.params);
      setResolveLimits(next.limits);
      setParamError(null);
      setParamOptions({});
      setDependentParamStates({});
      setOverlays({});
      clearSelection();
      if ('asOf' in next) {
        applyAsOfSelection(next.asOf ?? null);
      }
    },
    [applyAsOfSelection, clearSelection]
  );

  const discardCurrentWorkbenchSession = useCallback(
    async (threadIdOverride?: string | null) => {
      const sessionId = workbenchSession?.id;
      const resumeKey = workbenchSession?.resumeKey ?? workbenchResumeKey;
      const nextThreadId =
        threadIdOverride !== undefined ? threadIdOverride : workbenchThreadId ?? workbenchSession?.chatThreadId ?? null;
      if (orgId && sessionId) {
        await topologyApi.discardVisibilitySession({
          orgId,
          sessionId
        });
      }
      if (orgId && resumeKey) {
        clearTopologyWorkbenchResumeState(orgId, resumeKey);
      }
      syncWorkbenchSession(null, nextThreadId);
      setWorkbenchProposal(null);
      setWorkbenchRuntimeGraph(null);
      setWorkbenchRuntimeGraphSettled(false);
      setWorkbenchError(null);
      return nextThreadId;
    },
    [
      orgId,
      syncWorkbenchSession,
      topologyApi,
      workbenchResumeKey,
      workbenchSession?.chatThreadId,
      workbenchSession?.id,
      workbenchSession?.resumeKey,
      workbenchThreadId
    ]
  );

  const clearWorkbenchSessionLocally = useCallback(
    (options: { resumeKey?: string | null; threadId?: string | null } = {}) => {
      const resumeKey = options.resumeKey ?? workbenchSession?.resumeKey ?? workbenchResumeKey;
      const nextThreadId =
        options.threadId !== undefined
          ? options.threadId
          : workbenchThreadId ?? workbenchSession?.chatThreadId ?? null;
      if (orgId && resumeKey) {
        clearTopologyWorkbenchResumeState(orgId, resumeKey);
      }
      syncWorkbenchSession(null, nextThreadId);
      setWorkbenchProposal(null);
      setWorkbenchRuntimeGraph(null);
      setWorkbenchRuntimeGraphSettled(false);
      return nextThreadId;
    },
    [
      orgId,
      syncWorkbenchSession,
      workbenchResumeKey,
      workbenchSession?.chatThreadId,
      workbenchSession?.resumeKey,
      workbenchThreadId
    ]
  );

  const stageSavedViewMutation = useCallback(
    (response: TopologyV2ViewSaveResponse) => {
      setViews((current) => upsertTopologyView(current, response.view));
      setSavedViewResult(response);
      setSavedViewError(null);
      setSavedViewName(response.view.name ?? response.view.viewId ?? '');
      setSavedViewDescription(response.view.description ?? '');
      setSavedViewMode(response.view.scopeMode ?? savedViewMode);
    },
    [savedViewMode]
  );

  const finalizeSavedViewMutation = useCallback(
    async (response: TopologyV2ViewSaveResponse, options: { selectView?: boolean; reResolve?: boolean } = {}) => {
      const previousSessionId = workbenchSession?.id;
      const previousResumeKey = workbenchSession?.resumeKey ?? workbenchResumeKey;
      const nextThreadId = workbenchThreadId ?? workbenchSession?.chatThreadId ?? null;
      stageSavedViewMutation(response);
      clearWorkbenchSessionLocally({
        resumeKey: previousResumeKey,
        threadId: nextThreadId
      });
      setWorkbenchError(null);
      setPendingWorkbenchSwitch(null);
      setSaveDrawerOpen(false);
      if (options.selectView) {
        lastResolvedKeyRef.current = null;
        setSelectedViewId(response.view.viewId ?? '');
      } else if (options.reResolve && response.view.viewId === selectedViewId) {
        lastResolvedKeyRef.current = null;
        setAutoResolvePending(true);
      }
      if (orgId && previousSessionId) {
        try {
          await topologyApi.discardVisibilitySession({
            orgId,
            sessionId: previousSessionId
          });
        } catch (error) {
          setWorkbenchError(
            error instanceof Error
              ? `Saved view, but failed to clear the prior editing session on the server: ${error.message}`
              : 'Saved view, but failed to clear the prior editing session on the server.'
          );
        }
      }
    },
    [
      clearWorkbenchSessionLocally,
      orgId,
      selectedViewId,
      stageSavedViewMutation,
      topologyApi,
      workbenchResumeKey,
      workbenchSession?.chatThreadId,
      workbenchSession?.id,
      workbenchSession?.resumeKey,
      workbenchThreadId
    ]
  );

  const guardPendingWorkbenchSwitch = useCallback(
    (next: {
      viewId?: string;
      scopeType?: string;
      scopeId?: string;
      params?: Record<string, ParamValue>;
      limits?: Record<string, unknown>;
      asOf?: string | null;
    }): boolean => {
      if (!workbenchSession?.state?.base) {
        return false;
      }
      const nextScopeType = next.scopeType ?? scopeType;
      const nextScopeId =
        next.scopeId ??
        (nextScopeType === 'site'
          ? selectedSiteId
          : nextScopeType === 'org'
            ? (orgId ?? '')
            : scopeIdInput);
      const nextRequest: WorkbenchSwitchRequest = {
        viewId: next.viewId ?? selectedViewId,
        scopeType: nextScopeType,
        scopeId: nextScopeId,
        params: next.params ?? viewParams,
        limits: next.limits ?? resolveLimits,
        asOf: 'asOf' in next ? (next.asOf ?? null) : requestedAsOf
      };
      try {
        const nextBase = buildTopologyWorkbenchBase(nextRequest.viewId, nextRequest);
        const nextResumeKey = buildTopologyWorkbenchResumeKey(nextBase);
        if (shouldBlockTopologyWorkbenchSwitch(workbenchSession, nextResumeKey)) {
          setWorkbenchError('Apply or discard the current preview before switching topology context.');
          restoreWorkbenchBindingFromSession(workbenchSession);
          return true;
        }
        const hasDirtySession = Boolean(
          workbenchSession.pendingProposalId ||
            (workbenchSession.historyDepth ?? 0) > 0 ||
            (workbenchSession.state.layers?.length ?? 0) > 0 ||
            (workbenchSession.state.graphOverlays?.length ?? 0) > 0
        );
        if (!hasDirtySession || workbenchSession.resumeKey === nextResumeKey) {
          return false;
        }
        setPendingWorkbenchSwitch(nextRequest);
        setWorkbenchError(null);
        return true;
      } catch {
        if (workbenchSession.pendingProposalId) {
          setWorkbenchError('Apply or discard the current preview before switching topology context.');
          restoreWorkbenchBindingFromSession(workbenchSession);
          return true;
        }
        return false;
      }
    },
    [
      orgId,
      resolveLimits,
      restoreWorkbenchBindingFromSession,
      scopeIdInput,
      scopeType,
      selectedSiteId,
      selectedViewId,
      requestedAsOf,
      viewParams,
      workbenchSession
    ]
  );

  useEffect(() => {
    if (embedded) {
      syncWorkbenchSession(null);
      setWorkbenchError(null);
      return;
    }
    if (!workbenchBase || !workbenchResumeKey) {
      syncWorkbenchSession(null);
      setWorkbenchOverlayState(emptyTopologyOverlayState());
      setWorkbenchProposal(null);
      return;
    }
    if (workbenchSession?.resumeKey === workbenchResumeKey) {
      return;
    }
    syncWorkbenchSession(null);
    setWorkbenchOverlayState(emptyTopologyOverlayState(workbenchBase));
    setWorkbenchProposal(null);
  }, [embedded, syncWorkbenchSession, workbenchBase, workbenchResumeKey, workbenchSession?.resumeKey]);

  useEffect(() => {
    if (
      embedded ||
      !orgId ||
      !workbenchResumeKey ||
      !workbenchSession?.id ||
      workbenchSession.resumeKey !== workbenchResumeKey
    ) {
      return;
    }
    writeTopologyWorkbenchResumeState(orgId, workbenchResumeKey, {
      sessionId: workbenchSession.id,
      threadId: workbenchThreadId ?? workbenchSession.chatThreadId,
      bindingHash: workbenchSession.bindingHash
    });
  }, [
    embedded,
    orgId,
    workbenchResumeKey,
    workbenchSession?.bindingHash,
    workbenchSession?.chatThreadId,
    workbenchSession?.id,
    workbenchSession?.resumeKey,
    workbenchThreadId
  ]);

  useEffect(() => {
    if (embedded || !orgId || !workbenchBase || !workbenchResumeKey) {
      setWorkbenchRestoring(false);
      return;
    }
    if (workbenchSession?.resumeKey === workbenchResumeKey) {
      setWorkbenchRestoring(false);
      return;
    }
    const resumeState = readTopologyWorkbenchResumeState(orgId, workbenchResumeKey);
    if (!resumeState) {
      setWorkbenchRestoring(false);
      return;
    }
    if (!resumeState.sessionId) {
      syncWorkbenchSession(null, resumeState.threadId ?? null);
      setWorkbenchRestoring(false);
      return;
    }

    let cancelled = false;
    setWorkbenchRestoring(true);
    void topologyApi
      .getVisibilitySession({
        orgId,
        sessionId: resumeState.sessionId
      })
      .then((session) => {
        if (cancelled) {
          return;
        }
        const sessionResumeKey = session.state?.base ? buildTopologyWorkbenchResumeKey(session.state.base) : null;
        const matches =
          session.workspaceFamily === 'topology_workbench' &&
          session.status === 'active' &&
          sessionResumeKey === workbenchResumeKey;
        if (!matches) {
          if (resumeState.threadId) {
            writeTopologyWorkbenchResumeState(orgId, workbenchResumeKey, {
              threadId: resumeState.threadId,
              bindingHash: resumeState.bindingHash
            });
          } else {
            clearTopologyWorkbenchResumeState(orgId, workbenchResumeKey);
          }
          syncWorkbenchSession(null, resumeState.threadId ?? null);
          return;
        }
        syncWorkbenchSession(session, session.chatThreadId ?? resumeState.threadId ?? null);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        if (resumeState.threadId) {
          writeTopologyWorkbenchResumeState(orgId, workbenchResumeKey, {
            threadId: resumeState.threadId,
            bindingHash: resumeState.bindingHash
          });
        } else {
          clearTopologyWorkbenchResumeState(orgId, workbenchResumeKey);
        }
        syncWorkbenchSession(null, resumeState.threadId ?? null);
      })
      .finally(() => {
        if (!cancelled) {
          setWorkbenchRestoring(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    embedded,
    orgId,
    syncWorkbenchSession,
    topologyApi,
    workbenchBase,
    workbenchResumeKey,
    workbenchSession?.resumeKey
  ]);

  useEffect(() => {
    if (embedded || !orgId || !workbenchSession?.id || !workbenchSession.pendingProposalId) {
      setWorkbenchProposal(null);
      const nextOverlayState = deriveVisibilitySessionOverlayState(workbenchSession ?? null);
      workbenchOverlayStateRef.current = nextOverlayState;
      preserveWorkbenchRuntimeGraphRef.current = false;
      setWorkbenchOverlayState(nextOverlayState);
      return;
    }
    let cancelled = false;
    void topologyApi
      .getVisibilityProposal({
        orgId,
        sessionId: workbenchSession.id,
        proposalId: workbenchSession.pendingProposalId
      })
      .then((proposal) => {
        if (!cancelled) {
          const nextOverlayState = deriveVisibilitySessionOverlayState(workbenchSession, proposal);
          setWorkbenchProposal(proposal);
          workbenchOverlayStateRef.current = nextOverlayState;
          preserveWorkbenchRuntimeGraphRef.current = false;
          setWorkbenchOverlayState(nextOverlayState);
          setWorkbenchRuntimeGraph(null);
          setWorkbenchRuntimeGraphSettled(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          const nextOverlayState = deriveVisibilitySessionOverlayState(workbenchSession);
          setWorkbenchProposal(null);
          workbenchOverlayStateRef.current = nextOverlayState;
          preserveWorkbenchRuntimeGraphRef.current = false;
          setWorkbenchOverlayState(nextOverlayState);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    embedded,
    orgId,
    topologyApi,
    workbenchSession,
    workbenchSession?.id,
    workbenchSession?.pendingProposalId
  ]);

  useEffect(() => {
    if (embedded || !orgId || !workbenchSession?.id) {
      workbenchStreamAbortRef.current?.abort();
      workbenchStreamAbortRef.current = null;
      return;
    }

    workbenchStreamAbortRef.current?.abort();
    const controller = new AbortController();
    workbenchStreamAbortRef.current = controller;
    const streamUrl = buildOrgUrl(basePath, orgId, `visibility/sessions/${workbenchSession.id}/stream`);

    void streamSse(
      streamUrl,
      {
        onEvent: (eventType, data) => {
          const parsed = (() => {
            try {
              return parseTopologyWorkbenchStreamFrame(data, eventType);
            } catch {
              return null;
            }
          })();
          if (!parsed) {
            return;
          }
          const payload = parsed.payload;
          const frameType = parsed.type;
          if (!frameType) {
            return;
          }

          if (frameType === 'session_state') {
            const event = payload as unknown as VisibilitySessionStateEventData;
            if (event.sessionId !== workbenchSession.id) {
              return;
            }
            const nextSession = workbenchSessionRef.current
              ? mergeVisibilitySessionStateEvent(workbenchSessionRef.current, event)
              : null;
            const nextOverlayState = visibilitySessionStateToOverlayState(event.visibleState);
            const preserveRuntimeGraph = shouldPreserveWorkbenchRuntimeGraph(
              workbenchSessionRef.current,
              workbenchOverlayStateRef.current,
              nextOverlayState,
              event.pendingProposalId
            );
            preserveWorkbenchRuntimeGraphRef.current = preserveRuntimeGraph;
            workbenchSessionRef.current = nextSession;
            setWorkbenchSession(nextSession);
            if (event.chatThreadId) {
              setWorkbenchThreadId(event.chatThreadId);
            }
            workbenchOverlayStateRef.current = nextOverlayState;
            setWorkbenchOverlayState(nextOverlayState);
            setWorkbenchRuntimeGraph((current) => (preserveRuntimeGraph ? current : null));
            if (!preserveRuntimeGraph) {
              setWorkbenchRuntimeGraphSettled(false);
            }
            if (!event.pendingProposalId) {
              setWorkbenchProposal(null);
            }
            return;
          }

          if (frameType === 'overlay_status') {
            const event = payload as unknown as VisibilityOverlayStatusEventData;
            if (event.sessionId !== workbenchSession.id) {
              return;
            }
            setWorkbenchOverlayState((current) =>
              applyTopologyOverlayStatus(current, {
                artifactId: workbenchSession.id ?? 'visibility-session',
                overlayRevision: event.revision,
                layers: event.layers as TopologyArtifactOverlayLayerStatusschema[]
              })
            );
            return;
          }

          if (frameType === 'patch') {
            const ops = Array.isArray(payload.ops) ? (payload.ops as TopologyV2SseOp[]) : [];
            if (ops.length === 0) {
              return;
            }
            setWorkbenchRuntimeGraph((current) => {
              const baseGraph = current ?? workbenchVisibleGraphRef.current;
              if (!baseGraph) {
                return current;
              }
              return applyTopologyOpsToGraph(
                baseGraph as unknown as TopologyStreamGraph,
                ops
              ) as unknown as TopologyV2Graph;
            });
            setWorkbenchRuntimeGraphSettled(true);
          }
        }
      },
      controller.signal
    ).catch((error) => {
      if (!controller.signal.aborted) {
        console.warn('[topology-v2] workbench stream closed', { error });
      }
    });

    return () => {
      controller.abort();
    };
  }, [basePath, embedded, orgId, workbenchSession?.id]);

  const ensureWorkbenchSession = useCallback(
    async (threadId: string): Promise<VisibilitySession> => {
      if (!orgId || !workbenchBase || !workbenchResumeKey) {
        throw new Error('Resolve a topology view before chatting.');
      }
      if (workbenchSession?.id && workbenchSession.resumeKey === workbenchResumeKey) {
        if (!workbenchThreadId) {
          setWorkbenchThreadId(threadId);
        }
        return workbenchSession;
      }
      const session = await topologyApi.createOrResumeVisibilitySession({
        orgId,
        visibilitySessionCreateRequest: {
          workspaceFamily: 'topology_workbench',
          resumeKey: workbenchResumeKey,
          base: workbenchBase,
          selection: buildTopologyWorkbenchSelection(selectedElements),
          chatThreadId: threadId
        }
      });
      syncWorkbenchSession(session, threadId);
      return session;
    },
    [
      orgId,
      selectedElements,
      syncWorkbenchSession,
      topologyApi,
      workbenchBase,
      workbenchResumeKey,
      workbenchSession,
      workbenchThreadId
    ]
  );

  useEffect(() => {
    if (
      !mockTopologyAttachmentDemoEnabled ||
      !orgId ||
      !workbenchBase ||
      !workbenchResumeKey ||
      workbenchRestoring ||
      resolveLoading
    ) {
      return;
    }
    if (
      workbenchBase.viewId !== MOCK_TOPOLOGY_ATTACHMENT_VIEW_ID ||
      workbenchBase.scope.scopeType !== 'site' ||
      workbenchBase.scope.scopeId !== MOCK_TOPOLOGY_ATTACHMENT_SCOPE_ID
    ) {
      return;
    }
    if (
      workbenchSession?.resumeKey === workbenchResumeKey &&
      (workbenchThreadId === MOCK_TOPOLOGY_ATTACHMENT_THREAD_ID ||
        workbenchSession.chatThreadId === MOCK_TOPOLOGY_ATTACHMENT_THREAD_ID)
    ) {
      mockTopologySessionBootstrappedRef.current = workbenchResumeKey;
      return;
    }
    if (mockTopologySessionBootstrappedRef.current === workbenchResumeKey) {
      return;
    }

    let cancelled = false;
    mockTopologySessionBootstrappedRef.current = workbenchResumeKey;
    void ensureWorkbenchSession(MOCK_TOPOLOGY_ATTACHMENT_THREAD_ID)
      .then((session) => {
        if (cancelled) {
          return;
        }
        syncWorkbenchSession(session, session.chatThreadId ?? MOCK_TOPOLOGY_ATTACHMENT_THREAD_ID);
      })
      .catch(() => {
        if (!cancelled && mockTopologySessionBootstrappedRef.current === workbenchResumeKey) {
          mockTopologySessionBootstrappedRef.current = null;
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    ensureWorkbenchSession,
    mockTopologyAttachmentDemoEnabled,
    orgId,
    resolveLoading,
    syncWorkbenchSession,
    workbenchBase,
    workbenchResumeKey,
    workbenchRestoring,
    workbenchSession?.chatThreadId,
    workbenchSession?.resumeKey,
    workbenchThreadId
  ]);

  const buildWorkbenchInteractContext = useCallback(
    (session: VisibilitySession) => buildTopologyWorkbenchChatInteractContext(session, selectedElements),
    [selectedElements]
  );

  const runWorkbenchSessionAction = useCallback(
    async (action: string, executor: () => Promise<VisibilitySession>) => {
      setWorkbenchBusyAction(action);
      setWorkbenchError(null);
      try {
        const session = await executor();
        syncWorkbenchSession(session, workbenchThreadId);
      } catch (error) {
        setWorkbenchError(error instanceof Error ? error.message : `Failed to ${action} workbench change.`);
      } finally {
        setWorkbenchBusyAction(null);
      }
    },
    [syncWorkbenchSession, workbenchThreadId]
  );

  const handleApplyProposal = useCallback(() => {
    const proposalId = workbenchProposal?.id ?? workbenchSession?.pendingProposalId;
    const sessionId = workbenchSession?.id;
    if (!orgId || !sessionId || !proposalId) {
      return;
    }
    void runWorkbenchSessionAction('apply', () =>
      topologyApi.applyVisibilityProposal({
        orgId,
        sessionId,
        proposalId
      })
    );
  }, [orgId, runWorkbenchSessionAction, topologyApi, workbenchProposal?.id, workbenchSession?.id, workbenchSession?.pendingProposalId]);

  const handleRejectProposal = useCallback(() => {
    const proposalId = workbenchProposal?.id ?? workbenchSession?.pendingProposalId;
    const sessionId = workbenchSession?.id;
    if (!orgId || !sessionId || !proposalId) {
      return;
    }
    void runWorkbenchSessionAction('reject', () =>
      topologyApi.rejectVisibilityProposal({
        orgId,
        sessionId,
        proposalId
      })
    );
  }, [orgId, runWorkbenchSessionAction, topologyApi, workbenchProposal?.id, workbenchSession?.id, workbenchSession?.pendingProposalId]);

  const handleUndoProposal = useCallback(() => {
    const sessionId = workbenchSession?.id;
    if (!orgId || !sessionId) {
      return;
    }
    void runWorkbenchSessionAction('undo', () =>
      topologyApi.undoVisibilitySession({
        orgId,
        sessionId
      })
    );
  }, [orgId, runWorkbenchSessionAction, topologyApi, workbenchSession?.id]);

  const handleDiscardSession = useCallback(() => {
    const sessionId = workbenchSession?.id;
    if (!orgId || !sessionId) {
      return;
    }
    const confirmed = window.confirm('Abort this topology session and discard all applied and pending changes?');
    if (!confirmed) {
      return;
    }
    setWorkbenchBusyAction('discard-session');
    setWorkbenchError(null);
    void discardCurrentWorkbenchSession()
      .then(() => {
        setWorkbenchError(null);
      })
      .catch((error) => {
        setWorkbenchError(error instanceof Error ? error.message : 'Failed to abort the topology session.');
      })
      .finally(() => {
        setWorkbenchBusyAction(null);
      });
  }, [discardCurrentWorkbenchSession, orgId, workbenchSession?.id]);

  const handleCreateCustomView = useCallback(async () => {
    if (!orgId || !workbenchSession?.id) {
      return;
    }
    setSavedViewBusyAction('create');
    setSavedViewError(null);
    try {
      const request: TopologyV2ViewCreateRequest = {
        name: savedViewName.trim(),
        description: savedViewDescription.trim() || undefined,
        saveMode: savedViewMode,
        source: {
          kind: 'visibility_session',
          sessionId: workbenchSession.id
        }
      };
      const response = await visibilityApi.orgsOrgIdTopologyViewsPost({
        orgId,
        topologyV2ViewCreateRequest: request
      });
      await finalizeSavedViewMutation(response, { selectView: true });
    } catch (error) {
      setSavedViewError(error instanceof Error ? error.message : 'Failed to save custom topology view.');
    } finally {
      setSavedViewBusyAction(null);
    }
  }, [
    finalizeSavedViewMutation,
    orgId,
    savedViewDescription,
    savedViewMode,
    savedViewName,
    visibilityApi,
    workbenchSession?.id
  ]);

  const handleUpdateCustomViewMetadata = useCallback(async () => {
    if (!orgId || !activeView || !isCustomTopologyView(activeView)) {
      return;
    }
    setSavedViewBusyAction('update-metadata');
    setSavedViewError(null);
    try {
      const request: TopologyV2ViewUpdateRequest = {
        name: savedViewName.trim() || undefined,
        description: savedViewDescription.trim() || undefined
      };
      const response = await visibilityApi.orgsOrgIdTopologyViewsViewIdPatch({
        orgId,
        viewId: activeView.viewId,
        topologyV2ViewUpdateRequest: request
      });
      stageSavedViewMutation(response);
    } catch (error) {
      setSavedViewError(error instanceof Error ? error.message : 'Failed to update custom topology view.');
    } finally {
      setSavedViewBusyAction(null);
    }
  }, [activeView, orgId, savedViewDescription, savedViewName, stageSavedViewMutation, visibilityApi]);

  const handleReplaceCustomView = useCallback(async () => {
    if (!orgId || !activeView || !isCustomTopologyView(activeView) || !workbenchSession?.id) {
      return;
    }
    setSavedViewBusyAction('replace');
    setSavedViewError(null);
    try {
      const request: TopologyV2ViewUpdateRequest = {
        name: savedViewName.trim() || undefined,
        description: savedViewDescription.trim() || undefined,
        saveMode: savedViewMode,
        source: {
          kind: 'visibility_session',
          sessionId: workbenchSession.id
        }
      };
      const response = await visibilityApi.orgsOrgIdTopologyViewsViewIdPatch({
        orgId,
        viewId: activeView.viewId,
        topologyV2ViewUpdateRequest: request
      });
      await finalizeSavedViewMutation(response, { reResolve: true });
    } catch (error) {
      setSavedViewError(error instanceof Error ? error.message : 'Failed to replace saved topology view.');
    } finally {
      setSavedViewBusyAction(null);
    }
  }, [
    activeView,
    finalizeSavedViewMutation,
    orgId,
    savedViewDescription,
    savedViewMode,
    savedViewName,
    visibilityApi,
    workbenchSession?.id
  ]);

  const handleDeleteCustomView = useCallback(async () => {
    if (!orgId || !activeView || !isCustomTopologyView(activeView) || savedViewBusyAction === 'delete') {
      return;
    }
    setSavedViewBusyAction('delete');
    setSavedViewError(null);
    try {
      await visibilityApi.orgsOrgIdTopologyViewsViewIdDelete({
        orgId,
        viewId: activeView.viewId
      });
      const nextViewId = pickTopologyViewAfterDelete(views, activeView.viewId, activeView.baseTemplateViewId ?? null);
      setViews((current) => removeTopologyView(current, activeView.viewId));
      setSavedViewResult(null);
      setSavedOverlayState(emptyTopologyOverlayState());
      lastResolvedKeyRef.current = null;
      setSelectedViewId(nextViewId);
      setDeleteViewConfirmationOpen(false);
      setDeleteToast({ tone: 'success', message: `${activeView.name ?? 'Saved view'} deleted successfully.` });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete saved topology view.';
      setSavedViewError(message);
      setDeleteToast({ tone: 'error', message });
    } finally {
      setSavedViewBusyAction(null);
    }
  }, [activeView, orgId, savedViewBusyAction, views, visibilityApi]);

  const handleDiscardSessionAndSwitch = useCallback(() => {
    if (!pendingWorkbenchSwitch) {
      return;
    }
    const nextRequest = pendingWorkbenchSwitch;
    setWorkbenchBusyAction('discard-session');
    setWorkbenchError(null);
    void discardCurrentWorkbenchSession()
      .then(() => {
        setPendingWorkbenchSwitch(null);
        applyWorkbenchSwitch(nextRequest);
      })
      .catch((error) => {
        setWorkbenchError(error instanceof Error ? error.message : 'Failed to discard the current session.');
      })
      .finally(() => {
        setWorkbenchBusyAction(null);
      });
  }, [applyWorkbenchSwitch, discardCurrentWorkbenchSession, pendingWorkbenchSwitch]);

  const handleStayOnCurrentWorkbench = useCallback(() => {
    setPendingWorkbenchSwitch(null);
  }, []);

  const handleSaveBeforeSwitch = useCallback(() => {
    setPendingWorkbenchSwitch(null);
    setSaveDrawerOpen(true);
  }, []);

  useEffect(() => {
    if (!orgId) {
      setSites([]);
      setSitesError(null);
      setSelectedSiteId('');
      return;
    }
    setSitesLoading(true);
    setSitesError(null);
    fetchOrgResource<SitesListResponse>(basePath, orgId, 'sites?limit=500')
      .then((response: SitesListResponse) => {
        const nextSites: Site[] = response.items ?? [];
        setSites(nextSites);
        const first = nextSites.find((site) => typeof site.id === 'string')?.id ?? '';
        if (first) {
          setSelectedSiteId((prev) => (prev ? prev : first));
        }
      })
      .catch((error) => {
        const message = error instanceof Error ? error.message : 'Failed to load sites';
        setSitesError(message);
        setSites([]);
      })
      .finally(() => setSitesLoading(false));
  }, [basePath, fetchOrgResource, orgId]);

  useEffect(() => {
    const forced = requestedInitialSiteId;
    if (!forced) {
      return;
    }
    setSelectedSiteId(forced);
  }, [requestedInitialSiteId]);

  useEffect(() => {
    if (!requestedScopeType) {
      return;
    }
    setScopeType(requestedScopeType);
    if (requestedScopeType === 'site') {
      if (requestedScopeId) {
        setSelectedSiteId(requestedScopeId);
      }
      return;
    }
    if (requestedScopeType === 'org') {
      setScopeIdInput('');
      return;
    }
    setScopeIdInput(requestedScopeId);
  }, [requestedScopeId, requestedScopeType]);

  useEffect(() => {
    if (currentScopeBoundScope || scopeType !== 'site' || sitesLoading || sortedSites.length === 0) {
      return;
    }
    const hasSelectedSite = sortedSites.some((site) => (site.id ?? '').trim() === selectedSiteId);
    if (!hasSelectedSite) {
      setSelectedSiteId(sortedSites[0]?.id ?? '');
    }
  }, [currentScopeBoundScope, scopeType, selectedSiteId, sitesLoading, sortedSites]);

  useEffect(() => {
    if (!orgId) {
      setViews([]);
      setViewsError(null);
      setSelectedViewId('');
      return;
    }
    setViewsLoading(true);
    setViewsError(null);
    loadViews()
      .then((items) => {
        setViews(items);
        const forcedView = requestedViewId;
        const forcedExists = forcedView.length > 0 && items.some((item) => item.viewId === forcedView);
        const mockPreferred =
          mockTopologyAttachmentDemoEnabled && forcedView.length === 0
            ? items.find((item) => item.viewId === 'l3_membership')?.viewId
            : '';
        const preferred =
          mockPreferred || items.find((item) => item.viewId === 'org_overview')?.viewId || items[0]?.viewId || '';
        const first = forcedExists ? forcedView : preferred;
        setSelectedViewId((prev) => (prev && items.some((item) => item.viewId === prev) ? prev : first));
      })
      .catch((error) => {
        const message = error instanceof Error ? error.message : 'Failed to load topology views';
        setViewsError(message);
        setViews([]);
        setSelectedViewId('');
      })
      .finally(() => setViewsLoading(false));
  }, [loadViews, mockTopologyAttachmentDemoEnabled, orgId, requestedViewId]);

  useEffect(() => {
    if (!mockTopologyAttachmentDemoEnabled || mockTopologyDemoDefaultsAppliedRef.current) {
      return;
    }
    if (requestedViewId || requestedScopeType || requestedScopeId || routeAsOf) {
      return;
    }
    if (viewsLoading || sitesLoading || views.length === 0 || sortedSites.length === 0) {
      return;
    }
    const hasDemoView = views.some((item) => item.viewId === MOCK_TOPOLOGY_ATTACHMENT_VIEW_ID);
    const hasDemoSite = sortedSites.some((site) => (site.id ?? '').trim() === MOCK_TOPOLOGY_ATTACHMENT_SCOPE_ID);
    if (!hasDemoView || !hasDemoSite) {
      mockTopologyDemoDefaultsAppliedRef.current = true;
      return;
    }
    mockTopologyDemoDefaultsAppliedRef.current = true;
    lastResolvedKeyRef.current = null;
    setSelectedViewId(MOCK_TOPOLOGY_ATTACHMENT_VIEW_ID);
    setScopeType('site');
    setSelectedSiteId(MOCK_TOPOLOGY_ATTACHMENT_SCOPE_ID);
    setScopeIdInput('');
    applyAsOfSelection(MOCK_TOPOLOGY_ATTACHMENT_AS_OF);
  }, [
    applyAsOfSelection,
    mockTopologyAttachmentDemoEnabled,
    requestedScopeId,
    requestedScopeType,
    requestedViewId,
    routeAsOf,
    sitesLoading,
    sortedSites,
    views,
    viewsLoading
  ]);

  useEffect(() => {
    if (!activeView) {
      setViewParams({});
      setParamError(null);
      setParamOptions({});
      setDependentParamStates({});
      setResolveError(null);
      setScopeType('site');
      setScopeIdInput('');
      setSavedViewName('');
      setSavedViewDescription('');
      setSavedViewMode('reusable');
      setSavedViewError(null);
      setSavedViewResult(null);
      return;
    }
    const defaults = buildDefaultParams(activeView);
    setViewParams(defaults);
    setParamError(null);
    setResolveError(null);
    const availableScopes = scopeTypesForView(activeView);
    const initialScope = availableScopes[0] ?? 'site';
    const allowedScopes = availableScopes.length > 0 ? availableScopes : [initialScope];
    const nextScopeType = effectiveScopeTypeForView(currentScopeBoundScope?.scopeType ?? initialScope, allowedScopes);
    setScopeType((prev) => effectiveScopeTypeForView(currentScopeBoundScope?.scopeType ?? prev, allowedScopes));
    if (currentScopeBoundScope?.scopeType === 'site' && nextScopeType === 'site') {
      setSelectedSiteId(currentScopeBoundScope.scopeId);
      setScopeIdInput('');
    } else if (currentScopeBoundScope && nextScopeType !== 'site' && nextScopeType !== 'org') {
      setScopeIdInput(currentScopeBoundScope.scopeId);
    } else {
      setScopeIdInput('');
    }
    setSavedViewName(activeView.name ?? activeView.viewId ?? '');
    setSavedViewDescription(activeView.description ?? '');
    setSavedViewMode(activeView.scopeMode ?? 'reusable');
    setSavedViewError(null);
    setSavedViewResult((current) => (current?.view?.viewId === activeView.viewId ? current : null));
  }, [activeView?.viewId, currentScopeBoundScope?.scopeId, currentScopeBoundScope?.scopeType]);

  useEffect(() => {
    focusedResolveGenerationRef.current += 1;
    setBaseResolveRequest(null);
    setFocusedResolved(null);
    setFocusedResolveRequest(null);
    lastResolvedKeyRef.current = null;
    lastSeqRef.current = 0;
    baseLastSeqRef.current = 0;
    focusedLastSeqRef.current = 0;
  }, [selectedViewId, effectiveViewScopeType, effectiveViewScopeId]);

  useEffect(() => {
    if (!boundScopeError) {
      return;
    }
    focusedResolveGenerationRef.current += 1;
    setResolved(null);
    setBaseResolveRequest(null);
    setFocusedResolved(null);
    setFocusedResolveRequest(null);
    setOverlays({});
    clearSelection();
  }, [boundScopeError, clearSelection]);

  const handleResolve = useCallback(async () => {
    const requestScopeType = effectiveViewScopeType;
    const scopeId = effectiveViewScopeId;
    if (!orgId || !selectedViewId || !scopeId) {
      return;
    }

    const view = activeView;
    const paramsResult = view ? resolveParams(view, viewParams) : { params: {}, errors: [] };
    if (paramsResult.errors.length > 0) {
      setParamError(paramsResult.errors.join(' '));
      return;
    }

    setParamError(null);
    setResolveLoading(true);
    setResolveError(null);
    const generation = resolveGenerationRef.current + 1;
    resolveGenerationRef.current = generation;
    const isCurrentResolve = () => resolveGenerationRef.current === generation;
    try {
      const requestBody = sanitizeResolveRequestPayload({
        scopeType: requestScopeType,
        scopeId,
        ...(requestedAsOf ? { asOf: requestedAsOf } : {}),
        params: paramsResult.params,
        limits: resolveLimits
      });
      const nextWorkbenchBase = buildTopologyWorkbenchBase(selectedViewId, requestBody);
      const nextWorkbenchResumeKey = buildTopologyWorkbenchResumeKey(nextWorkbenchBase);
      if (shouldBlockTopologyWorkbenchSwitch(workbenchSession, nextWorkbenchResumeKey)) {
        setWorkbenchError('Apply or discard the current preview before switching topology context.');
        if (workbenchSession) {
          restoreWorkbenchBindingFromSession(workbenchSession);
        }
        return;
      }
      const payload = await visibilityApi.orgsOrgIdTopologyViewsViewIdResolvePost({
        orgId,
        viewId: selectedViewId,
        topologyV2ResolveRequest: {
          ...requestBody,
          ...(typeof requestBody.asOf === 'string' ? { asOf: new Date(requestBody.asOf) } : {})
        } as any
      });
      if (!isCurrentResolve()) {
        return;
      }
      const resolvedAsOf = requestedAsOf ? metaAsOf(payload?.meta) ?? requestedAsOf : null;
      const resolvedRequestBody = resolvedAsOf ? { ...requestBody, asOf: resolvedAsOf } : requestBody;
      focusedResolveGenerationRef.current += 1;
      setResolved(payload);
      setBaseResolveRequest(resolvedRequestBody);
      setFocusedResolved(null);
      setFocusedResolveRequest(null);
      setOverlays({});
      clearSelection();
      const meta = asRecord(payload?.meta);
      if (meta && typeof meta.lastEventId === 'number') {
        lastSeqRef.current = Math.max(0, meta.lastEventId);
      } else {
        lastSeqRef.current = 0;
      }
      baseLastSeqRef.current = lastSeqRef.current;
      focusedLastSeqRef.current = 0;
      lastResolvedKeyRef.current = buildResolveKey({
        viewId: selectedViewId,
        scopeType: requestScopeType,
        scopeId,
        params: paramsResult.params,
        limits: resolveLimits,
        asOf: requestedAsOf
      });
    } catch (error) {
      if (!isCurrentResolve()) {
        return;
      }
      const errorCode = await readResponseErrorCode(error);
      if (errorCode === 'invalid_param_combination') {
        const recoveredParams = recoverInvalidParamCombination(
          activeView,
          viewParams,
          buildEffectiveParamOptions(activeView, viewParams, paramOptions, dependentParamStates)
        );
        if (recoveredParams) {
          setDependentParamStates({});
          setDependentParamRefreshNonce((current) => current + 1);
          setParamError(null);
          setResolveError(null);
          setViewParams(recoveredParams);
          return;
        }
      }
      if (errorCode === 'base_graph_not_found') {
        focusedResolveGenerationRef.current += 1;
        setResolveError(null);
        setResolved(null);
        setBaseResolveRequest(null);
        setFocusedResolved(null);
        setFocusedResolveRequest(null);
        setOverlays({});
        clearSelection();
        lastSeqRef.current = 0;
        baseLastSeqRef.current = 0;
        focusedLastSeqRef.current = 0;
        return;
      }
      const message = error instanceof Error ? error.message : 'Failed to resolve view';
      focusedResolveGenerationRef.current += 1;
      setResolveError(message);
      setResolved(null);
      setBaseResolveRequest(null);
      setFocusedResolved(null);
      setFocusedResolveRequest(null);
      setOverlays({});
      clearSelection();
      lastSeqRef.current = 0;
      baseLastSeqRef.current = 0;
      focusedLastSeqRef.current = 0;
    } finally {
      if (isCurrentResolve()) {
        setResolveLoading(false);
      }
    }
  }, [
    activeView,
    effectiveViewScopeId,
    effectiveViewScopeType,
    orgId,
    resolveLimits,
    restoreWorkbenchBindingFromSession,
    selectedViewId,
    visibilityApi,
    viewParams,
    clearSelection,
    requestedAsOf,
    dependentParamStates,
    paramOptions,
    workbenchSession
  ]);

  useEffect(() => {
    const scopeId = effectiveViewScopeId;
    if (!orgId || !activeView || !scopeId || boundScopeError) {
      paramOptionSignatureRef.current = {};
      paramOptionLoadedSignatureRef.current = {};
      paramOptionRequestedSignatureRef.current = {};
      setParamOptions({});
      setDependentParamStates({});
      return;
    }
    let cancelled = false;
    const paramsWithOptions = visibleViewParams(activeView).filter(
      ([, spec]) => Boolean(spec.options) && !isDependentParamStateParam(spec)
    );
    if (paramsWithOptions.length === 0) {
      paramOptionSignatureRef.current = {};
      paramOptionLoadedSignatureRef.current = {};
      paramOptionRequestedSignatureRef.current = {};
      setParamOptions({});
      setDependentParamStates({});
      return;
    }

    const currentSignatures: Record<string, string> = {};
    const nextStates: Record<string, ParamOptionsState> = {};
    const fetchTargets: Array<{ name: string; signature: string; resource: string; groupedParent: boolean }> = [];
    let shouldClearDependentParamState = false;

    paramsWithOptions.forEach(([name, spec]) => {
      const dependencies = optionParamDependencies(name, spec);
      const groupedParent = isGroupedParentOptionsParam(spec);
      const signature = optionParamSignature(
        activeView.viewId,
        effectiveViewScopeType,
        scopeId,
        dependencies,
        viewParams,
        {
          asOf: requestedAsOf,
          refreshNonce: groupedParent ? dependentParamRefreshNonce : null
        }
      );
      currentSignatures[name] = signature;

      if (dependencies.some((dependency) => !isFilledParamValue(viewParams[dependency]))) {
        delete paramOptionLoadedSignatureRef.current[name];
        delete paramOptionRequestedSignatureRef.current[name];
        nextStates[name] = { items: [], loading: false, error: null, blocked: true };
        return;
      }

      if (
        paramOptionLoadedSignatureRef.current[name] === signature ||
        paramOptionRequestedSignatureRef.current[name] === signature
      ) {
        return;
      }

      const query = new URLSearchParams({
        scopeType: effectiveViewScopeType,
        scopeId
      });
      if (requestedAsOf) {
        query.set('asOf', requestedAsOf);
      }
      const selectedParams = Object.fromEntries(
        dependencies
          .filter((dependency) => isFilledParamValue(viewParams[dependency]))
          .map((dependency) => [dependency, viewParams[dependency]])
      );
      if (Object.keys(selectedParams).length > 0) {
        query.set('params', JSON.stringify(selectedParams));
      }
      paramOptionRequestedSignatureRef.current[name] = signature;
      nextStates[name] = { items: [], loading: true, error: null, blocked: false };
      shouldClearDependentParamState = shouldClearDependentParamState || groupedParent;
      fetchTargets.push({
        name,
        signature,
        resource: `topology/views/${encodeURIComponent(activeView.viewId)}/params/${encodeURIComponent(name)}/options?${query.toString()}`,
        groupedParent
      });
    });

    paramOptionSignatureRef.current = currentSignatures;
    Object.keys(paramOptionLoadedSignatureRef.current).forEach((name) => {
      if (!(name in currentSignatures)) {
        delete paramOptionLoadedSignatureRef.current[name];
      }
    });
    Object.keys(paramOptionRequestedSignatureRef.current).forEach((name) => {
      if (!(name in currentSignatures)) {
        delete paramOptionRequestedSignatureRef.current[name];
      }
    });
    if (shouldClearDependentParamState) {
      setDependentParamStates({});
    }
    setParamOptions((prev) => {
      let changed = Object.keys(prev).some((name) => !(name in currentSignatures));
      const next: Record<string, ParamOptionsState> = {};
      paramsWithOptions.forEach(([name]) => {
        if (nextStates[name]) {
          next[name] = nextStates[name];
          if (prev[name] !== nextStates[name]) {
            changed = true;
          }
          return;
        }
        if (prev[name]) {
          next[name] = prev[name];
          return;
        }
        next[name] = { items: [], loading: false, error: null, blocked: false };
        changed = true;
      });
      return changed ? next : prev;
    });

    fetchTargets.forEach(async ({ name, signature, resource, groupedParent }) => {
      try {
        const payload = await fetchOrgResource<{ items?: TopologyV2ParamOptionItem[]; meta?: unknown }>(basePath, orgId, resource);
        if (cancelled || paramOptionSignatureRef.current[name] !== signature) {
          return;
        }
        paramOptionLoadedSignatureRef.current[name] = signature;
        delete paramOptionRequestedSignatureRef.current[name];
        if (groupedParent) {
          setDependentParamStates(parseDependentParamStates(payload.meta));
        }
        setParamOptions((prev) => ({
          ...prev,
          [name]: {
            items: payload.items ?? [],
            loading: false,
            error: null,
            blocked: false
          }
        }));
      } catch (error) {
        if (cancelled || paramOptionSignatureRef.current[name] !== signature) {
          return;
        }
        delete paramOptionLoadedSignatureRef.current[name];
        delete paramOptionRequestedSignatureRef.current[name];
        const message = error instanceof Error ? error.message : 'Failed to load options';
        if (groupedParent) {
          setDependentParamStates({});
        }
        setParamOptions((prev) => ({
          ...prev,
          [name]: {
            items: [],
            loading: false,
            error: message,
            blocked: false
          }
        }));
      }
    });

    return () => {
      fetchTargets.forEach(({ name, signature }) => {
        if (
          paramOptionRequestedSignatureRef.current[name] === signature &&
          paramOptionLoadedSignatureRef.current[name] !== signature
        ) {
          delete paramOptionRequestedSignatureRef.current[name];
        }
      });
      cancelled = true;
    };
  }, [
    activeView,
    basePath,
    boundScopeError,
    dependentParamRefreshNonce,
    effectiveViewScopeId,
    effectiveViewScopeType,
    fetchOrgResource,
    orgId,
    requestedAsOf,
    viewParams
  ]);

  const effectiveParamOptions = useMemo(
    () => buildEffectiveParamOptions(activeView, viewParams, paramOptions, dependentParamStates),
    [activeView, dependentParamStates, paramOptions, viewParams]
  );

  useEffect(() => {
    if (!activeView) {
      return;
    }
    const entries = Object.entries(activeView.params ?? {});
    if (entries.length === 0) {
      return;
    }

    let updated = false;
    const nextValues: Record<string, ParamValue> = { ...viewParams };

    entries.forEach(([name, spec]) => {
      const dependencies = optionParamDependencies(name, spec);
      const optionsState = effectiveParamOptions[name];
      const current = nextValues[name];
      const isEmpty = current === undefined || current === null || current === '';

      if (dependencies.some((dependency) => !isFilledParamValue(viewParams[dependency]))) {
        if (!isEmpty) {
          delete nextValues[name];
          updated = true;
        }
        return;
      }

      if (!spec.options || !optionsState || optionsState.loading || optionsState.error) {
        return;
      }

      if (!isEmpty) {
        const currentValue = String(current);
        const matchesOption = optionsState.items.some((item) => item.value === currentValue);
        if (!matchesOption) {
          delete nextValues[name];
          updated = true;
        }
        return;
      }

      if (!spec.required || optionsState.items.length === 0) {
        return;
      }
      nextValues[name] = optionsState.items[0]?.value ?? '';
      updated = true;
    });

    if (updated) {
      setViewParams(nextValues);
      setAutoResolvePending(true);
    }
  }, [activeView, effectiveParamOptions, viewParams]);

  useEffect(() => {
    if (!autoResolvePending || !activeView) {
      return;
    }
    if (timeMode === 'fixed' && !requestedAsOf) {
      return;
    }
    const paramsResult = resolveParams(activeView, viewParams);
    if (paramsResult.errors.length > 0) {
      return;
    }
    setAutoResolvePending(false);
    void handleResolve();
  }, [activeView, autoResolvePending, handleResolve, requestedAsOf, timeMode, viewParams]);

  useEffect(() => {
    const scopeId = effectiveViewScopeId;
    if (!orgId || !selectedViewId || !activeView || !scopeId || boundScopeError) {
      return;
    }
    if (timeMode === 'fixed' && !requestedAsOf) {
      return;
    }
    const paramsResult = resolveParams(activeView, viewParams);
    if (paramsResult.errors.length > 0) {
      return;
    }
    const resolveKey = buildResolveKey({
      viewId: selectedViewId,
      scopeType: effectiveViewScopeType,
      scopeId,
      params: paramsResult.params,
      limits: resolveLimits,
      asOf: requestedAsOf
    });
    if (lastResolvedKeyRef.current === resolveKey) {
      return;
    }
    lastResolvedKeyRef.current = resolveKey;
    void handleResolve();
  }, [activeView, boundScopeError, effectiveViewScopeId, effectiveViewScopeType, handleResolve, orgId, requestedAsOf, resolveLimits, selectedViewId, timeMode, viewParams]);

  useEffect(() => {
    setLayoutResetCounter((count) => count + 1);
  }, [orgId, scopeIdInput, scopeType, selectedSiteId, selectedViewId]);

  useEffect(() => {
    const streamScopeType =
      typeof activeResolveRequest?.scopeType === 'string' ? activeResolveRequest.scopeType : resolveScopeType;
    const scopeId =
      typeof activeResolveRequest?.scopeId === 'string'
        ? activeResolveRequest.scopeId
        : effectiveScopeIdForResolve(streamScopeType, orgId, selectedSiteId, scopeIdInput);
    const baseAsOf = typeof activeResolveRequest?.asOf === 'string' ? activeResolveRequest.asOf.trim() : '';
    if (!orgId || !selectedViewId || !scopeId || !activeResolved?.graph || !activeResolveRequest) {
      streamAbortRef.current?.abort();
      streamAbortRef.current = null;
      return;
    }
    if (timeMode !== 'live' || baseAsOf) {
      streamAbortRef.current?.abort();
      streamAbortRef.current = null;
      return;
    }

    const params = asRecord(activeResolveRequest.params) ?? {};
    const limits = asRecord(activeResolveRequest.limits) ?? {};
    const streamingFocused = activeResolveRequest === focusedResolveRequest;

    streamAbortRef.current?.abort();
    const controller = new AbortController();
    streamAbortRef.current = controller;

    const streamUrl = new URL(buildOrgUrl(basePath, orgId, `topology/views/${selectedViewId}/stream`));
    streamUrl.searchParams.set('scopeType', streamScopeType);
    streamUrl.searchParams.set('scopeId', scopeId);
    if (Object.keys(params).length > 0) {
      streamUrl.searchParams.set('params', JSON.stringify(params));
    }
    if (Object.keys(limits).length > 0) {
      streamUrl.searchParams.set('limits', JSON.stringify(limits));
    }
    if (lastSeqRef.current > 0) {
      streamUrl.searchParams.set('fromSeq', String(lastSeqRef.current));
    }
    const handleEvent = (eventType: string, data: string) => {
      let frame: TopologyV2SseFrame | null = null;
      try {
        frame = TopologyViewStreamFrameFromJSON(JSON.parse(data));
      } catch (error) {
        console.warn('[topology-v2] invalid SSE payload', { error, data });
        return;
      }
      if (frame.viewId && frame.viewId !== selectedViewId) {
        return;
      }
      if (frame.scopeType && frame.scopeType !== streamScopeType) {
        return;
      }
      if (frame.scopeId && frame.scopeId !== scopeId) {
        return;
      }
      if (typeof frame.seq === 'number') {
        lastSeqRef.current = Math.max(lastSeqRef.current, frame.seq);
        if (streamingFocused) {
          focusedLastSeqRef.current = Math.max(focusedLastSeqRef.current, frame.seq);
        } else {
          baseLastSeqRef.current = Math.max(baseLastSeqRef.current, frame.seq);
        }
      }

      const frameType = resolveTopologyFrameType(eventType, frame.type);
      const payload = asRecord(frame.data) ?? {};
      if (frameType === 'snapshot') {
        const applySnapshot = (prev: TopologyV2ResolveResponse | null) =>
          (extractTopologySnapshot(payload, prev as unknown as TopologyStreamResolveResponse | null) as TopologyV2ResolveResponse | null) ??
          prev;
        if (streamingFocused) {
          setFocusedResolved((prev) => applySnapshot(prev));
        } else {
          setResolved((prev) => applySnapshot(prev));
        }
        return;
      }
      if (frameType === 'patch') {
        const ops = (payload.ops ?? []) as TopologyV2SseOp[];
        if (!Array.isArray(ops) || ops.length === 0) {
          return;
        }
        const applyPatch = (prev: TopologyV2ResolveResponse | null) => {
          if (!prev?.graph) {
            return prev;
          }
          return {
            ...prev,
            graph: applyTopologyOpsToGraph(prev.graph as unknown as TopologyStreamGraph, ops) as unknown as TopologyV2Graph
          };
        };
        if (streamingFocused) {
          setFocusedResolved((prev) => applyPatch(prev));
        } else {
          setResolved((prev) => applyPatch(prev));
        }
        setOverlays((prev) => {
          if (!prev || Object.keys(prev).length === 0) {
            return prev;
          }
          let changed = false;
          const next: typeof prev = { ...prev };
          Object.entries(prev).forEach(([key, overlay]) => {
            const graph = overlay?.response?.graph;
            if (!graph) {
              return;
            }
            const updatedGraph = applyTopologyOpsToGraph(graph as unknown as TopologyStreamGraph, ops) as unknown as TopologyV2Graph;
            if (updatedGraph === graph) {
              return;
            }
            changed = true;
            next[key] = {
              ...overlay,
              response: { ...overlay.response, graph: updatedGraph }
            };
          });
          return changed ? next : prev;
        });
        return;
      }
      if (frameType === 'overlay_status') {
        setSavedOverlayState((current) =>
          setTopologyOverlayLayerStatuses(
            current,
            Array.isArray(payload.layers) ? (payload.layers as TopologyArtifactOverlayLayerStatusschema[]) : []
          )
        );
        return;
      }
      if (frameType === 'error') {
        console.warn('[topology-v2] SSE error', { payload });
      }
    };

    void streamSse(
      streamUrl.toString(),
      {
        onEvent: handleEvent
      },
      controller.signal
    ).catch((error) => {
      if (controller.signal.aborted) {
        return;
      }
      console.warn('[topology-v2] SSE stream closed', { error });
    });

    return () => {
      controller.abort();
    };
  }, [
    activeResolveRequest,
    basePath,
    focusedResolveRequest,
    orgId,
    resolveScopeType,
    scopeIdInput,
    selectedSiteId,
    selectedViewId,
    timeMode
  ]);

  const evaluateWhen = useCallback(
    (
      when: Record<string, unknown> | undefined,
      contextMeta: Record<string, unknown>,
      node?: TopologyV2Node,
      edge?: TopologyV2Edge
    ): boolean => topologyLegendWhenMatches(when, contextMeta, node, edge),
    []
  );

  const pickNodeUI = useCallback(
    (node: TopologyV2Node): Record<string, unknown> => {
      const ownerKey = merged?.overlayKeyByNodeId?.[node.id] ?? 'base';
      const legend = merged?.legendByOverlayKey?.[ownerKey] ?? merged?.legendByOverlayKey?.base ?? baseLegend;
      const contextMeta =
        merged?.contextByOverlayKey?.[ownerKey] ?? merged?.contextByOverlayKey?.base ?? baseContextMeta;

      const rules = Array.isArray(legend?.nodeRules) ? legend.nodeRules : [];
      for (const rule of rules) {
        if (evaluateWhen(rule.when as Record<string, unknown> | undefined, contextMeta, node, undefined)) {
          return (rule.ui ?? {}) as Record<string, unknown>;
        }
      }
      return {};
    },
    [baseContextMeta, baseLegend, evaluateWhen, merged]
  );

  const pickEdgeUI = useCallback(
    (edge: TopologyV2Edge): Record<string, unknown> => {
      const ownerKey = merged?.overlayKeyByEdgeId?.[edge.id] ?? 'base';
      const legend = merged?.legendByOverlayKey?.[ownerKey] ?? merged?.legendByOverlayKey?.base ?? baseLegend;
      const contextMeta =
        merged?.contextByOverlayKey?.[ownerKey] ?? merged?.contextByOverlayKey?.base ?? baseContextMeta;

      const rules = Array.isArray(legend?.edgeRules) ? legend.edgeRules : [];
      for (const rule of rules) {
        if (evaluateWhen(rule.when as Record<string, unknown> | undefined, contextMeta, undefined, edge)) {
          return (rule.ui ?? {}) as Record<string, unknown>;
        }
      }
      return {};
    },
    [baseContextMeta, baseLegend, evaluateWhen, merged]
  );

  const getDeep = useCallback((obj: any, path: string[]): unknown => {
    let cur: any = obj;
    for (const part of path) {
      if (!cur || typeof cur !== 'object') {
        return undefined;
      }
      cur = cur[part];
    }
    return cur;
  }, []);

  const evalExpression = useCallback(
    (
      value: unknown,
      env: {
        request: Record<string, unknown>;
        context: Record<string, unknown>;
        node?: TopologyV2Node;
        edge?: TopologyV2Edge;
      }
    ): unknown => {
      if (typeof value !== 'string') {
        return value;
      }
      const resolveToken = (token: string): unknown => {
        const expr = token.trim();
        if (!expr) {
          return undefined;
        }
        if (expr === 'node.id') {
          return env.node?.id;
        }
        if (expr === 'edge.id') {
          return env.edge?.id;
        }
        if (expr === 'edge.fromId') {
          return env.edge?.fromId;
        }
        if (expr === 'edge.toId') {
          return env.edge?.toId;
        }
        if (expr.startsWith('node.labels.')) {
          return nodeLabelValue(env.node, expr.slice('node.labels.'.length));
        }
        if (expr.startsWith('edge.labels.')) {
          return env.edge?.labels?.[expr.slice('edge.labels.'.length)];
        }
        if (expr.startsWith('node.properties.')) {
          return getDeep(env.node?.properties ?? {}, expr.slice('node.properties.'.length).split('.'));
        }
        if (expr.startsWith('edge.properties.')) {
          return getDeep(env.edge?.properties ?? {}, expr.slice('edge.properties.'.length).split('.'));
        }
        if (expr.startsWith('request.')) {
          return getDeep(env.request, expr.slice('request.'.length).split('.'));
        }
        if (expr.startsWith('context.')) {
          return getDeep(env.context, expr.slice('context.'.length).split('.'));
        }
        return undefined;
      };

      const trimmed = value.trim();
      const exactTemplate = trimmed.match(/^\$\{([^}]+)\}$/);
      if (exactTemplate) {
        const resolved = resolveToken(exactTemplate[1] ?? '');
        return resolved ?? '';
      }
      if (trimmed.includes('${')) {
        return trimmed.replace(/\$\{([^}]+)\}/g, (_match, tokenExpr: string) => {
          const resolved = resolveToken(tokenExpr);
          if (resolved === undefined || resolved === null) {
            return '';
          }
          return String(resolved);
        });
      }
      const expr = trimmed;
      if (expr === 'node.id') {
        return env.node?.id;
      }
      if (expr === 'edge.id') {
        return env.edge?.id;
      }
      if (expr === 'edge.fromId') {
        return env.edge?.fromId;
      }
      if (expr === 'edge.toId') {
        return env.edge?.toId;
      }
      if (expr.startsWith('node.labels.')) {
        return nodeLabelValue(env.node, expr.slice('node.labels.'.length));
      }
      if (expr.startsWith('edge.labels.')) {
        return env.edge?.labels?.[expr.slice('edge.labels.'.length)];
      }
      if (expr.startsWith('node.properties.')) {
        return getDeep(env.node?.properties ?? {}, expr.slice('node.properties.'.length).split('.'));
      }
      if (expr.startsWith('edge.properties.')) {
        return getDeep(env.edge?.properties ?? {}, expr.slice('edge.properties.'.length).split('.'));
      }
      if (expr.startsWith('request.')) {
        return getDeep(env.request, expr.slice('request.'.length).split('.'));
      }
      if (expr.startsWith('context.')) {
        return getDeep(env.context, expr.slice('context.'.length).split('.'));
      }
      return value;
    },
    [getDeep]
  );

  const evalDeep = useCallback(
    (
      value: unknown,
      env: { request: Record<string, unknown>; context: Record<string, unknown>; node?: TopologyV2Node; edge?: TopologyV2Edge }
    ): unknown => {
      const v = evalExpression(value, env);
      if (Array.isArray(v)) {
        return v.map((item) => evalDeep(item, env));
      }
      const rec = asRecord(v);
      if (!rec) {
        return v;
      }
      const out: Record<string, unknown> = {};
      Object.entries(rec).forEach(([k, vv]) => {
        out[k] = evalDeep(vv, env);
      });
      return out;
    },
    [evalExpression]
  );

  const applyPatch = useCallback(
    (
      baseRequest: Record<string, unknown>,
      patch: TopologyV2ResolveRequestPatch | undefined,
      env: { request: Record<string, unknown>; context: Record<string, unknown>; node?: TopologyV2Node; edge?: TopologyV2Edge }
    ): Record<string, unknown> => {
      if (!patch) {
        return baseRequest;
      }
      const next: Record<string, unknown> = { ...baseRequest };

      const scopeType = evalDeep(patch.scopeType, env);
      const scopeId = evalDeep(patch.scopeId, env);
      const asOf = evalDeep(patch.asOf, env);
      const overlayKey = evalDeep(patch.overlayKey, env);

      if (typeof scopeType === 'string' && scopeType) {
        next.scopeType = scopeType;
      }
      if (typeof scopeId === 'string' && scopeId) {
        next.scopeId = scopeId;
      }
      if (typeof asOf === 'string' && asOf) {
        next.asOf = asOf;
      }
      if (typeof overlayKey === 'string' && overlayKey) {
        next.overlayKey = overlayKey;
      }

      const params = evalDeep(patch.params, env);
      if (params && typeof params === 'object' && !Array.isArray(params)) {
        next.params = {
          ...(asRecord(baseRequest.params) ?? {}),
          ...(params as Record<string, unknown>)
        };
      }

      const limits = evalDeep(patch.limits, env);
      if (limits && typeof limits === 'object' && !Array.isArray(limits)) {
        next.limits = {
          ...(asRecord(baseRequest.limits) ?? {}),
          ...(limits as Record<string, unknown>)
        };
      }

      return next;
    },
    [evalDeep]
  );

  const findFirstDrilldown = useCallback(
    (
      drilldowns: TopologyV2Drilldown[],
      contextMeta: Record<string, unknown>,
      node?: TopologyV2Node,
      edge?: TopologyV2Edge,
      mode?: 'overlay'
    ): TopologyV2Drilldown | null => {
      for (const d of drilldowns) {
        if (!d?.name || !d?.viewId) {
          continue;
        }
        if (mode === 'overlay' && d.mode && d.mode !== 'overlay') {
          continue;
        }
        if (evaluateWhen(d.appliesWhen, contextMeta, node, edge)) {
          return d;
        }
      }
      return null;
    },
    [evaluateWhen]
  );

  const replaceWithDrilldownView = useCallback(
    (
      drilldown: TopologyV2Drilldown,
      ownerRequest: Record<string, unknown>,
      ownerContext: Record<string, unknown>,
      node?: TopologyV2Node,
      edge?: TopologyV2Edge
    ) => {
      if (!views.some((view) => view.viewId === drilldown.viewId)) {
        return;
      }
      const env = { request: ownerRequest, context: ownerContext, node, edge };
      const withStatic = applyPatch(ownerRequest, drilldown._static, env);
      const nextRequest = applyPatch(withStatic, drilldown.dynamic, env);
      const nextScopeType = typeof nextRequest.scopeType === 'string' ? nextRequest.scopeType.trim() : '';
      const nextScopeId = typeof nextRequest.scopeId === 'string' ? nextRequest.scopeId.trim() : '';
      const nextParams: Record<string, ParamValue> = {};
      const params = asRecord(nextRequest.params) ?? {};
      Object.entries(params).forEach(([key, value]) => {
        if (typeof value === 'string' || typeof value === 'boolean') {
          nextParams[key] = value;
        }
      });
      const nextLimits = asRecord(nextRequest.limits) ?? {};
      if (
        guardPendingWorkbenchSwitch({
          viewId: drilldown.viewId,
          scopeType: nextScopeType || undefined,
          scopeId: nextScopeId || undefined,
          params: nextParams,
          limits: nextLimits,
          asOf: typeof nextRequest.asOf === 'string' ? nextRequest.asOf : requestedAsOf
        })
      ) {
        return;
      }

      setSelectedViewId(drilldown.viewId);
      if (nextScopeType) {
        setScopeType(nextScopeType);
      }
      if (nextScopeType === 'site') {
        setSelectedSiteId(nextScopeId);
      } else if (nextScopeId) {
        setScopeIdInput(nextScopeId);
      }
      setViewParams(nextParams);
      setResolveLimits(nextLimits);
      setParamError(null);
      setParamOptions({});
      setDependentParamStates({});
      setOverlays({});
      clearSelection();
      if (typeof nextRequest.asOf === 'string') {
        applyAsOfSelection(nextRequest.asOf);
      }
    },
    [applyAsOfSelection, applyPatch, clearSelection, guardPendingWorkbenchSwitch, requestedAsOf, views]
  );

  const resolveOverlay = useCallback(
    async (
      ownerKey: string,
      drilldown: TopologyV2Drilldown,
      ownerRequest: Record<string, unknown>,
      ownerContext: Record<string, unknown>,
      node?: TopologyV2Node,
      edge?: TopologyV2Edge
    ) => {
      if (!orgId) {
        return;
      }
      const env = { request: ownerRequest, context: ownerContext, node, edge };
      const withStatic = applyPatch(ownerRequest, drilldown._static, env);
      const nextRequest = applyPatch(withStatic, drilldown.dynamic, env);

      let overlayKey = typeof nextRequest.overlayKey === 'string' ? (nextRequest.overlayKey as string) : '';
      if (!overlayKey && edge?.id) {
        overlayKey = `${drilldown.viewId}:edge:${edge.id}`;
      }
      if (!overlayKey && node?.id) {
        overlayKey = `${drilldown.viewId}:node:${node.id}`;
      }
      if (!overlayKey) {
        setResolveError('Overlay drilldown is missing overlayKey and no fallback key could be derived.');
        return;
      }

      if (overlays[overlayKey]) {
        setOverlays((prev) => {
          const next = { ...prev };
          const toDelete = new Set<string>([overlayKey]);
          let changed = true;
          while (changed) {
            changed = false;
            Object.entries(next).forEach(([key, overlay]) => {
              if (toDelete.has(key)) {
                return;
              }
              if (overlay && toDelete.has(overlay.ownerKey)) {
                toDelete.add(key);
                changed = true;
              }
            });
          }
          toDelete.forEach((key) => {
            delete next[key];
          });
          return next;
        });
        return;
      }

      const edgePresentation = topologyPresentationProps(edge?.properties);
      const preserveOwnerEdge = edgePresentation?.preserveOnOverlay === true;
      const hiddenEdgeIds = preserveOwnerEdge ? [] : merged?.edgeGroupIndex?.[ownerKey]?.[overlayKey] ?? [];
      const computedHiddenEdgeIds =
        hiddenEdgeIds.length > 0 ? hiddenEdgeIds : preserveOwnerEdge ? [] : edge?.id ? [edge.id] : node?.id ? [node.id] : [];

      const url = buildOrgUrl(basePath, orgId, `topology/views/${drilldown.viewId}/resolve`);
      setResolveLoading(true);
      setResolveError(null);
      try {
        // overlayKey is a UI-only bookkeeping field; do not send it to the backend.
        const { overlayKey: _overlayKey, ...wireRequest } = nextRequest as Record<string, unknown> & {
          overlayKey?: unknown;
        };
        const requestPayload = sanitizeResolveRequestPayload(wireRequest);
        const postResolve = (payload: Record<string, unknown>) =>
          fetchWithAuthRetry(url, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
          });

        let response = await postResolve(requestPayload);
        if (!response.ok && response.status === 400) {
          try {
            const body = (await response.clone().json()) as Record<string, unknown>;
            const code = typeof body.error === 'string' ? body.error.trim() : '';
            const limits = asRecord(requestPayload.limits);
            const hasHopKeys = Boolean(limits && ('rootNodeId' in limits || 'hops' in limits));
            if (code === 'invalid_json' && hasHopKeys) {
              const fallbackPayload = sanitizeResolveRequestPayload({
                ...requestPayload,
                limits: {
                  ...(limits ?? {}),
                  rootNodeId: undefined,
                  hops: undefined
                }
              });
              response = await postResolve(fallbackPayload);
            }
          } catch {
            // ignore parse failure and continue with original error path
          }
        }
        if (!response.ok) {
          let detail = '';
          try {
            const payload = (await response.clone().json()) as Record<string, unknown>;
            const code = typeof payload.error === 'string' ? payload.error.trim() : '';
            const message = typeof payload.message === 'string' ? payload.message.trim() : '';
            const best = code || message;
            if (best) {
              detail = ` (${best})`;
            }
          } catch {
            // keep generic status-only error
          }
          throw new ApiResponseError(response, `Resolve failed with status ${response.status}${detail}`);
        }
        const contentType = response.headers.get('Content-Type') ?? '';
        if (!contentType.toLowerCase().includes('application/json')) {
          throw new ApiResponseError(response, 'Expected JSON response but received a different content type.');
        }
        const payload = TopologyV2ResolveResponseFromJSON(await response.json());
        setOverlays((prev) => ({
          ...prev,
          [overlayKey]: {
            viewId: drilldown.viewId,
            request: nextRequest,
            response: payload,
            ownerKey,
            hiddenEdgeIds: computedHiddenEdgeIds
          }
        }));
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to resolve drilldown';
        setResolveError(message);
      } finally {
        setResolveLoading(false);
      }
    },
    [applyPatch, basePath, fetchWithAuthRetry, merged, orgId, overlays]
  );

  const updateSelection = useCallback((event: ReactMouseEvent, next: SelectedElement) => {
    const additive = Boolean((event as any)?.shiftKey);
    setSelectedElements((prev) => {
      const exists = prev.some((item) => item.kind === next.kind && item.id === next.id);
      if (additive) {
        return exists ? prev.filter((item) => !(item.kind === next.kind && item.id === next.id)) : [...prev, next];
      }
      if (prev.length === 1 && exists) {
        return [];
      }
      return [next];
    });
  }, []);

  const collapseOverlayCascade = useCallback((overlayKey: string) => {
    setOverlays((prev) => {
      const next = { ...prev };
      const toDelete = new Set<string>([overlayKey]);
      let changed = true;
      while (changed) {
        changed = false;
        Object.entries(next).forEach(([key, overlay]) => {
          if (toDelete.has(key)) {
            return;
          }
          if (overlay && toDelete.has(overlay.ownerKey)) {
            toDelete.add(key);
            changed = true;
          }
        });
      }
      toDelete.forEach((key) => {
        delete next[key];
      });
      return next;
    });
  }, []);

  const computeOverlayKey = useCallback(
    (
      ownerRequest: Record<string, unknown>,
      ownerContext: Record<string, unknown>,
      drilldown: TopologyV2Drilldown,
      node?: TopologyV2Node,
      edge?: TopologyV2Edge
    ): string => {
      const env = { request: ownerRequest, context: ownerContext, node, edge };
      const withStatic = applyPatch(ownerRequest, drilldown._static, env);
      const nextRequest = applyPatch(withStatic, drilldown.dynamic, env);
      let overlayKey = typeof nextRequest.overlayKey === 'string' ? (nextRequest.overlayKey as string) : '';
      if (!overlayKey && edge?.id) {
        overlayKey = `${drilldown.viewId}:edge:${edge.id}`;
      }
      if (!overlayKey && node?.id) {
        overlayKey = `${drilldown.viewId}:node:${node.id}`;
      }
      return overlayKey;
    },
    [applyPatch]
  );

  const handleNodeSelect = useCallback(
    (event: ReactMouseEvent, nodeId: string) => {
      updateSelection(event, { kind: 'node', id: nodeId });
    },
    [updateSelection]
  );

  const handleEdgeSelect = useCallback(
    (event: ReactMouseEvent, edgeId: string) => {
      updateSelection(event, { kind: 'edge', id: edgeId });
    },
    [updateSelection]
  );

  const handleNodeDoubleClick = useCallback(
    (event: ReactMouseEvent, nodeId: string) => {
      event.preventDefault();
      if (!merged) {
        return;
      }
      const ownerKey = merged.overlayKeyByNodeId[nodeId] ?? 'base';
      const drilldowns = merged.drilldownsByOverlayKey[ownerKey] ?? [];
      const contextMeta = merged.contextByOverlayKey[ownerKey] ?? {};
      const request = merged.requestByOverlayKey[ownerKey];
      const node = merged.nodeById.get(nodeId);
      if (!node || !request) {
        return;
      }
      const isFold = Boolean((event as any)?.shiftKey);
      if (isFold) {
        if (ownerKey !== 'base') {
          collapseOverlayCascade(ownerKey);
          return;
        }
        const drilldown = findFirstDrilldown(drilldowns, contextMeta, node, undefined, 'overlay');
        if (!drilldown) {
          return;
        }
        const overlayKey = computeOverlayKey(request, contextMeta, drilldown, node, undefined);
        if (overlayKey && overlays[overlayKey]) {
          collapseOverlayCascade(overlayKey);
        }
        return;
      }

      const drilldown = findFirstDrilldown(drilldowns, contextMeta, node, undefined);
      if (!drilldown) {
        return;
      }
      if (drilldown.mode === 'replace') {
        if (drilldown.viewId === selectedViewId) {
          return;
        }
        replaceWithDrilldownView(drilldown, request, contextMeta, node, undefined);
        return;
      }
      if (drilldown.mode && drilldown.mode !== 'overlay') {
        return;
      }
      void resolveOverlay(ownerKey, drilldown, request, contextMeta, node, undefined);
    },
    [
      collapseOverlayCascade,
      computeOverlayKey,
      findFirstDrilldown,
      merged,
      overlays,
      replaceWithDrilldownView,
      resolveOverlay,
      selectedViewId,
    ]
  );

  const selectionAutoFocusRequest = useMemo(() => {
    if (!merged || !selectedViewId) {
      return null;
    }

    const nodeIds = Array.from(selectedNodeIds);
    const edgeIds = Array.from(selectedEdgeIds);
    if (nodeIds.length + edgeIds.length !== 1) {
      return null;
    }

    const selectedNodeId = nodeIds[0];
    const selectedEdgeId = edgeIds[0];
    const node = selectedNodeId ? merged.nodeById.get(selectedNodeId) : undefined;
    const edge = selectedEdgeId ? merged.edgeById.get(selectedEdgeId) : undefined;
    if (!node && !edge) {
      return null;
    }

    let ownerKey = 'base';
    if (node) {
      ownerKey = merged.overlayKeyByNodeId[node.id] ?? 'base';
    } else if (edge) {
      ownerKey = merged.overlayKeyByEdgeId[edge.id] ?? 'base';
    }
    const drilldowns = merged.drilldownsByOverlayKey[ownerKey] ?? [];
    const contextMeta = merged.contextByOverlayKey[ownerKey] ?? {};
    const ownerRequest = merged.requestByOverlayKey[ownerKey];
    if (!ownerRequest) {
      return null;
    }

    const matchingDrilldowns = drilldowns.filter(
      (drilldown) =>
        drilldown.mode === 'replace' &&
        drilldown.viewId === selectedViewId &&
        evaluateWhen(drilldown.appliesWhen, contextMeta, node, edge)
    );
    if (matchingDrilldowns.length !== 1) {
      return null;
    }

    const match = matchingDrilldowns[0];
    const env = { request: ownerRequest, context: contextMeta, node, edge };
    const withStatic = applyPatch(ownerRequest, match?._static, env);
    return applyPatch(withStatic, match?.dynamic, env);
  }, [applyPatch, evaluateWhen, merged, selectedEdgeIds, selectedNodeIds, selectedViewId]);

  useEffect(() => {
    if (!orgId || !selectedViewId || !baseResolveRequest || !resolved?.graph) {
      return;
    }

    const requestKey = (request: Record<string, unknown> | null): string | null => {
      if (!request) {
        return null;
      }
      const scopeType = typeof request.scopeType === 'string' ? request.scopeType : '';
      const scopeId = typeof request.scopeId === 'string' ? request.scopeId : '';
      if (!scopeType || !scopeId) {
        return null;
      }
      return buildResolveKey({
        viewId: selectedViewId,
        scopeType,
        scopeId,
        params: asRecord(request.params) ?? {},
        limits: asRecord(request.limits) ?? {},
        asOf: typeof request.asOf === 'string' ? request.asOf : null
      });
    };

    if (!selectionAutoFocusRequest) {
      if (!focusedResolveRequest) {
        return;
      }
      focusedResolveGenerationRef.current += 1;
      lastSeqRef.current = baseLastSeqRef.current;
      focusedLastSeqRef.current = 0;
      setFocusedResolved(null);
      setFocusedResolveRequest(null);
      setOverlays({});
      return;
    }
    if (requestKey(selectionAutoFocusRequest) === requestKey(focusedResolveRequest)) {
      return;
    }

    const requestBody: Record<string, unknown> = selectionAutoFocusRequest;
    const generation = focusedResolveGenerationRef.current + 1;
    focusedResolveGenerationRef.current = generation;
    const isCurrentResolve = () => focusedResolveGenerationRef.current === generation;

    setResolveError(null);
    setFocusedResolveRequest(requestBody);
    void (async () => {
      try {
        const payload = await visibilityApi.orgsOrgIdTopologyViewsViewIdResolvePost({
          orgId,
          viewId: selectedViewId,
          topologyV2ResolveRequest: {
            ...requestBody,
            ...(typeof requestBody.asOf === 'string' ? { asOf: new Date(requestBody.asOf) } : {})
          } as any
        });
        if (!isCurrentResolve()) {
          return;
        }
        const meta = asRecord(payload?.meta);
        if (meta && typeof meta.lastEventId === 'number') {
          focusedLastSeqRef.current = Math.max(0, meta.lastEventId);
        } else {
          focusedLastSeqRef.current = 0;
        }
        lastSeqRef.current = focusedLastSeqRef.current;
        setFocusedResolved(payload);
        setOverlays({});
      } catch (error) {
        if (!isCurrentResolve()) {
          return;
        }
        const message = error instanceof Error ? error.message : 'Failed to resolve view';
        setResolveError(message);
      }
    })();
  }, [
    baseResolveRequest,
    focusedResolveRequest,
    orgId,
    resolved?.graph,
    selectionAutoFocusRequest,
    selectedViewId,
    visibilityApi
  ]);

  const handleEdgeDoubleClick = useCallback(
    (event: ReactMouseEvent, edgeId: string) => {
      event.preventDefault();
      if (!merged) {
        return;
      }
      const ownerKey = merged.overlayKeyByEdgeId[edgeId] ?? 'base';
      const drilldowns = merged.drilldownsByOverlayKey[ownerKey] ?? [];
      const contextMeta = merged.contextByOverlayKey[ownerKey] ?? {};
      const request = merged.requestByOverlayKey[ownerKey];
      const edge = merged.edgeById.get(edgeId);
      if (!edge || !request) {
        return;
      }
      const isFold = Boolean((event as any)?.shiftKey);
      if (isFold) {
        if (ownerKey !== 'base') {
          collapseOverlayCascade(ownerKey);
          return;
        }
        const drilldown = findFirstDrilldown(drilldowns, contextMeta, undefined, edge, 'overlay');
        if (!drilldown) {
          return;
        }
        const overlayKey = computeOverlayKey(request, contextMeta, drilldown, undefined, edge);
        if (overlayKey && overlays[overlayKey]) {
          collapseOverlayCascade(overlayKey);
        }
        return;
      }

      const drilldown = findFirstDrilldown(drilldowns, contextMeta, undefined, edge, 'overlay');
      if (!drilldown) {
        return;
      }
      void resolveOverlay(ownerKey, drilldown, request, contextMeta, undefined, edge);
    },
    [collapseOverlayCascade, computeOverlayKey, findFirstDrilldown, merged, overlays, resolveOverlay]
  );

  const handlePaneClick = useCallback(
    (event: ReactMouseEvent) => {
      if (Boolean((event as any)?.shiftKey)) {
        return;
      }
      if ((event as any)?.button !== undefined && (event as any).button !== 0) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (!target) {
        clearSelection();
        return;
      }
      const isPane =
        target.classList.contains('react-flow__pane') ||
        target.classList.contains('react-flow__viewport') ||
        target.classList.contains('react-flow');
      if (!isPane) {
        return;
      }
      clearSelection();
    },
    [clearSelection]
  );

  const activeResolveLayoutKey = useMemo(() => {
    if (!selectedViewId || !activeResolveRequest) {
      return 'base';
    }
    return buildResolveKey({
      viewId: selectedViewId,
      scopeType: typeof activeResolveRequest.scopeType === 'string' ? activeResolveRequest.scopeType : '',
      scopeId: typeof activeResolveRequest.scopeId === 'string' ? activeResolveRequest.scopeId : '',
      params: asRecord(activeResolveRequest.params) ?? {},
      limits: asRecord(activeResolveRequest.limits) ?? {},
      asOf: typeof activeResolveRequest.asOf === 'string' ? activeResolveRequest.asOf : null
    });
  }, [activeResolveRequest, selectedViewId]);
  const activeGraphId = activeResolved?.graph?.graphId ?? '';
  const topologyDiagramKey = `${selectedViewId || 'view'}-${layoutResetCounter}-${activeResolveLayoutKey}-${activeGraphId || 'base'}`;

  const removeSelectedElement = useCallback((kind: SelectedElement['kind'], id: string) => {
    setSelectedElements((prev) => prev.filter((item) => !(item.kind === kind && item.id === id)));
  }, []);
  const pendingProposalId = workbenchProposal?.id ?? workbenchSession?.pendingProposalId ?? null;
  const hasUndoableHistory = (workbenchSession?.historyDepth ?? 0) > 0;
  const isCustomSelectedView = isCustomTopologyView(activeView);
  const isBoundScopeLocked = Boolean(currentScopeBoundScope);
  const savedLayerCount = savedOverlayState.layers.length;
  const workbenchProposalSummary =
    workbenchProposal?.summary?.trim() ||
    workbenchProposal?.requestText?.trim() ||
    (pendingProposalId ? 'A topology workbench proposal is ready to review.' : '');
  const appliedWorkbenchOverlayState = useMemo(
    () => visibilitySessionStateToOverlayState(workbenchSession?.state),
    [workbenchSession?.state]
  );
  const appliedWorkbenchLayers = appliedWorkbenchOverlayState.layers;
  const hasDirtyWorkbenchSession = Boolean(
    workbenchSession?.id &&
      (pendingProposalId ||
        hasUndoableHistory ||
        appliedWorkbenchLayers.length > 0 ||
        (workbenchSession?.state?.graphOverlays?.length ?? 0) > 0)
  );
  const workbenchHiddenLayerIds = useMemo(
    () => new Set(workbenchOverlayState.localHiddenLayerIds),
    [workbenchOverlayState.localHiddenLayerIds]
  );
  const savedHiddenLayerIds = useMemo(() => new Set(savedOverlayState.localHiddenLayerIds), [savedOverlayState.localHiddenLayerIds]);
  const previewWorkbenchLayers = useMemo(() => {
    if (!pendingProposalId) {
      return [];
    }
    const appliedLayerIds = new Set(appliedWorkbenchLayers.map((layer) => layer.id));
    return workbenchOverlayState.layers.filter((layer) => !appliedLayerIds.has(layer.id));
  }, [appliedWorkbenchLayers, pendingProposalId, workbenchOverlayState.layers]);
  const hasAbortableSession = hasDirtyWorkbenchSession;
  const workbenchActionTitle = pendingProposalId
    ? 'Pending change'
    : hasUndoableHistory
      ? 'Applied changes'
      : hasAbortableSession
        ? 'Editing session'
        : '';
  const workbenchActionSubtitle = pendingProposalId
    ? workbenchProposalSummary
    : hasUndoableHistory
      ? 'Undo reverts the most recent applied change.'
      : hasAbortableSession
        ? 'Abort clears the current editing session and returns to the pristine base view.'
        : '';
  const workbenchDisabledReason = boundScopeError
    ? boundScopeError
    : !workbenchBase
    ? 'Resolve a topology view before chatting.'
    : resolveLoading
      ? 'Resolving topology…'
      : workbenchRestoring
        ? 'Restoring the current workbench session…'
        : null;
  const canSaveFromWorkbench = Boolean(orgId && workbenchSession?.id && !pendingProposalId && !resolveLoading && !workbenchRestoring);
  const savedViewBusy = Boolean(savedViewBusyAction);
  const savedViewScopeLabel = activeView?.scopeMode === 'current_scope' ? 'Current scope' : 'Reusable';
  const savedViewSummaryLines = useMemo(() => topologyViewSaveSummaryLines(savedViewResult ?? {}), [savedViewResult]);
  const totalLayerCount = savedLayerCount + appliedWorkbenchLayers.length + previewWorkbenchLayers.length;
  const pendingSwitchView = useMemo(
    () => views.find((view) => view.viewId === pendingWorkbenchSwitch?.viewId) ?? null,
    [pendingWorkbenchSwitch?.viewId, views]
  );
  const pendingSwitchScopeLabel = pendingWorkbenchSwitch
    ? `${formatScopeLabel(pendingWorkbenchSwitch.scopeType)} ${pendingWorkbenchSwitch.scopeId}`
    : '';
  const pendingSwitchSaveLabel = isCustomSelectedView ? 'Save changes first' : 'Save as custom view first';
  const workspaceStatusLabel = pendingProposalId
    ? 'Preview pending'
    : isCustomSelectedView
      ? 'Saved view'
      : hasUndoableHistory
        ? 'Applied changes'
        : null;
  const workspaceStatusTitle = pendingProposalId
    ? 'Apply or discard the current preview before continuing.'
    : isCustomSelectedView
      ? currentScopeBoundScope
        ? `${savedViewScopeLabel} private view bound to ${formatScopeLabel(currentScopeBoundScope.scopeType).toLowerCase()} ${currentScopeBoundScope.scopeId}.`
        : `${savedViewScopeLabel} private view`
      : hasUndoableHistory
        ? 'The current session has applied topology changes.'
        : workbenchDisabledReason ?? 'Use chat to manipulate the current topology.';
  const timeStatusLabel = timeMode === 'fixed' ? formatAsOfStatusLabel(asOfInput || requestedAsOf) : 'Live';
  const timeStatusTitle =
    timeMode === 'fixed'
      ? requestedAsOf
        ? 'Viewing a fixed historical topology snapshot. Live streaming is disabled.'
        : 'Choose a valid "As of" timestamp to resolve a historical snapshot.'
      : 'Viewing the live topology. Streaming updates remain enabled.';
  const chatBlockedReason = pendingProposalId
    ? 'Apply or discard the preview before asking for another topology change.'
    : workbenchDisabledReason;

  const toggleSavedLayerVisibility = useCallback((layerId: string, visible: boolean) => {
    setSavedOverlayState((current) => setTopologyLayerLocalVisibility(current, layerId, visible));
  }, []);

  const toggleWorkbenchLayerVisibility = useCallback((layerId: string, visible: boolean) => {
    setWorkbenchOverlayState((current) => setTopologyLayerLocalVisibility(current, layerId, visible));
  }, []);

  const renderLayerGroup = useCallback(
    (
      title: string,
      layers: TopologyOverlayRuntimeState['layers'],
      hiddenLayerIds: Set<string>,
      overlayState: TopologyOverlayRuntimeState,
      onToggleVisibility: (layerId: string, visible: boolean) => void
    ) => {
      if (layers.length === 0) {
        return null;
      }
      return (
        <div className="topology-workbench__layers-group">
          <div className="topology-workbench__layers-group-title">{title}</div>
          <div className="topology-workbench__layers-list">
            {layers.map((layer) => {
              const visible = layer.visible !== false && !hiddenLayerIds.has(layer.id);
              const layerStatus = overlayState.layerStatuses[layer.id];
              const statusLabel = topologyLayerStatusLabel(layerStatus?.status);
              const deliveryLabel = topologyLayerDeliveryLabel(layer, layerStatus);
              const deliveryTone = topologyLayerDeliveryTone(deliveryLabel);
              const relationshipSummary = formatTopologyRelationshipLayerMetadata(layer);
              const pathSummary = formatTopologyPathLayerMetadata(layer);
              const relatedViewBreadcrumb = formatTopologyPathRelatedViewBreadcrumb(layer);
              return (
                <div key={layer.id} className="topology-workbench__layer-row">
                  <div className="chat-artifact-topology__layer-row">
                    <span className="chat-artifact-topology__layer-label" title={layer.label}>
                      {layer.label}
                    </span>
                    <span className="chat-artifact-topology__layer-kind">{layer.kind}</span>
                    {deliveryLabel ? (
                      <span
                        className={`chat-artifact-topology__layer-delivery chat-artifact-topology__layer-delivery--${deliveryTone}`}
                        title={deliveryLabel === 'Live' ? 'Live layer that continues to refresh while the topology is visible.' : 'Fixed layer rendered from a point-in-time value.'}
                      >
                        {topologyLayerDeliveryIcon(deliveryLabel)}
                        {deliveryLabel}
                      </span>
                    ) : null}
                    <span
                      className={`chat-artifact-topology__layer-status chat-artifact-topology__layer-status--${statusLabel}`}
                      title={topologyLayerStatusTitle(layerStatus)}
                    >
                      {statusLabel}
                    </span>
                    <button
                      type="button"
                      className="chat-artifact-topology__layer-action"
                      aria-pressed={visible}
                      onClick={() => onToggleVisibility(layer.id, !visible)}
                    >
                      {visible ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  {relationshipSummary && (
                    <div className="topology-workbench__layer-relationship" title={relationshipSummary}>
                      {relationshipSummary}
                    </div>
                  )}
                  {pathSummary && (
                    <div className="topology-workbench__layer-relationship" title={pathSummary}>
                      {pathSummary}
                    </div>
                  )}
                  {relatedViewBreadcrumb && (
                    <div className="topology-workbench__layer-breadcrumb" title={relatedViewBreadcrumb}>
                      {relatedViewBreadcrumb}
                    </div>
                  )}
                  {layerStatus?.message && <div className="topology-workbench__layer-message">{layerStatus.message}</div>}
                </div>
              );
            })}
          </div>
        </div>
      );
    },
    []
  );

  const selectionPanel =
    merged && selectedElements.length > 0 ? (
      <div className="topology-v2__compare-panel">
        <div className="topology-v2__compare-panel-header">
          <div style={{ fontWeight: 800 }}>Selected ({selectedElements.length})</div>
          <div style={{ display: 'inline-flex', gap: '0.5rem', alignItems: 'center' }}>
            {Object.keys(overlays).length > 0 && (
              <button type="button" className="button button--secondary button--small" onClick={() => setOverlays({})}>
                Collapse all overlays
              </button>
            )}
            <button type="button" className="button button--secondary button--small" onClick={clearSelection}>
              Clear (Esc)
            </button>
          </div>
        </div>

        <div className="topology-v2__compare-panel-body">
          {selectedElements.map((item) => {
            const isNode = item.kind === 'node';
            const node = isNode ? merged.nodeById.get(item.id) : undefined;
            const edge = !isNode ? merged.edgeById.get(item.id) : undefined;
            const ownerKey = isNode ? merged.overlayKeyByNodeId[item.id] ?? 'base' : merged.overlayKeyByEdgeId[item.id] ?? 'base';
            const contextMeta = merged.contextByOverlayKey[ownerKey] ?? {};
            const request = merged.requestByOverlayKey[ownerKey];
            const drilldowns = (merged.drilldownsByOverlayKey[ownerKey] ?? []).filter(
              (d) => (!d.mode || d.mode === 'overlay') && evaluateWhen(d.appliesWhen, contextMeta, node, edge)
            );

            const selectedProperties = isNode ? node?.properties ?? {} : edge?.properties ?? {};
            const title = isNode
              ? topologyPresentationString(selectedProperties, 'selectionTitle', node?.displayName ?? node?.id ?? item.id)
              : topologyPresentationString(selectedProperties, 'selectionTitle', edge ? `${edge.fromId} → ${edge.toId}` : item.id);
            const subtitle = isNode
              ? topologyPresentationString(selectedProperties, 'selectionSubtitle', node?.labels?.level ? formatScopeLabel(node.labels.level) : 'Node')
              : topologyPresentationString(selectedProperties, 'selectionSubtitle', edge?.labels?.relation ? formatScopeLabel(edge.labels.relation) : 'Link');

            const embeddedPolicySection = extractTopologyEmbeddedPolicySection(selectedProperties);
            const providerEdgePorts = isNode
              ? normalizeProviderEdgePorts(
                  selectedProperties.providerEdgePorts ??
                    (selectedProperties.providerEdgePort ? [selectedProperties.providerEdgePort] : [])
                )
              : [];
            // These fields have dedicated generic sections below. Excluding the
            // duplicate raw payload keeps the bounded property list responsive
            // without interpreting any graph family or protocol.
            const propertyEntries = topologySelectionPropertyRows(selectedProperties, {
              excludeKeys: [
                ...(embeddedPolicySection ? ['rules', 'entries'] : []),
                ...(providerEdgePorts.length > 0 ? ['providerEdgePort', 'providerEdgePorts'] : [])
              ]
            });

            return (
              <div key={`${item.kind}:${item.id}`} className="topology-v2__compare-card">
                <div className="topology-v2__compare-card-header">
                  <div>
                    <div style={{ fontWeight: 800 }}>{title}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                      {subtitle}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="button button--ghost button--small"
                    aria-label="Remove from selection"
                    onClick={() => removeSelectedElement(item.kind, item.id)}
                  >
                    ✕
                  </button>
                </div>

                {ownerKey !== 'base' && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <button
                      type="button"
                      className="button button--secondary button--small"
                      onClick={() => collapseOverlayCascade(ownerKey)}
                    >
                      Collapse this overlay
                    </button>
                  </div>
                )}

                {drilldowns.length > 0 && request && (
                  <div style={{ marginTop: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {drilldowns.map((d) => {
                      const overlayKey = computeOverlayKey(request, contextMeta, d, node, edge);
                      const active = Boolean(overlayKey && overlays[overlayKey]);
                      const label = active ? `Collapse: ${d.name}` : d.name;
                      return (
                        <button
                          key={`${d.viewId}:${d.name}`}
                          type="button"
                          className="button button--secondary button--small"
                          onClick={() => void resolveOverlay(ownerKey, d, request, contextMeta, node, edge)}
                          disabled={resolveLoading}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                )}

                <div style={{ marginTop: '0.75rem' }}>
                  {providerEdgePorts.length > 0 && (
                    <div className="topology-v2__provider-edge-section">
                      <div className="topology-v2__provider-edge-section-title">
                        Provider Edge {providerEdgePorts.length === 1 ? 'Port' : 'Ports'} ({providerEdgePorts.length})
                      </div>
                      <ProviderEdgePortTable ports={providerEdgePorts} />
                    </div>
                  )}
                  <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>Properties</div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '0.25rem' }}>
                    {propertyEntries.length > 0 ? (
                      propertyEntries.map((row) => (
                        <li key={row.key} style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginLeft: `${row.depth * 8}px` }}>
                          <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{row.label}</span>
                          {!row.group ? <><span>: </span><span style={{ whiteSpace: 'pre-wrap' }}>{row.value}</span></> : null}
                        </li>
                      ))
                    ) : (
                      <li style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                        No properties
                      </li>
                    )}
                  </ul>
                </div>

                {embeddedPolicySection && (
                  <div className="topology-v2__policy-section">
                    <div className="topology-v2__policy-section-title">{embeddedPolicySection.title}</div>
                    <div className="topology-v2__policy-list">
                      {embeddedPolicySection.items.map((policyItem) => (
                        <div key={policyItem.key} className="topology-v2__policy-item">
                          <div className="topology-v2__policy-item-title">{policyItem.title}</div>
                          {policyItem.subtitle ? (
                            <div className="topology-v2__policy-item-subtitle">{policyItem.subtitle}</div>
                          ) : null}
                          {policyItem.chips.length > 0 ? (
                            <div className="topology-v2__policy-chip-list">
                              {policyItem.chips.map((chip) => (
                                <span key={`${policyItem.key}:${chip}`} className="topology-v2__policy-chip">
                                  {chip}
                                </span>
                              ))}
                            </div>
                          ) : null}
                          <TopologyPolicyRawDetails value={policyItem.rawValue} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    ) : null;

  if (!orgId) {
    if (embedded) {
      return <OrgModulePlaceholder intent="Activate an organization to explore topology views." />;
    }
    return (
      <OrgModuleLayout
        title="Topology v2"
        description="Organization and site topology views powered by the v2 view catalog."
        topSlot={<OrgModulePlaceholder intent="Select an organization to load topology views." />}
      >
        <OrgModulePlaceholder intent="Activate an organization to explore topology v2 views." />
      </OrgModuleLayout>
    );
  }

  const toolbarError = sitesError || viewsError || paramError;
  const topSlot = toolbarError ? <OrgModulePlaceholder intent={toolbarError} /> : undefined;
  const controlFields = !hideControls && (
    <div className="section-card topology-workbench__toolbar">
      <div className="topology-workbench__toolbar-fields">
        <label className="form-field">
          <span className="form-field__label">View</span>
          <select
            className="select"
            value={selectedViewId}
            onChange={(event) => {
              if (guardPendingWorkbenchSwitch({ viewId: event.target.value })) {
                return;
              }
              setSelectedViewId(event.target.value);
            }}
            disabled={viewsLoading || sortedViews.length === 0}
          >
            <option value="">{viewsLoading ? 'Loading views…' : 'Select view…'}</option>
            {groupedViews.system.length > 0 && (
              <optgroup label="System views">
                {groupedViews.system.map((view) => (
                  <option key={view.viewId} value={view.viewId}>
                    {formatViewCatalogLabel(view)}
                  </option>
                ))}
              </optgroup>
            )}
            {groupedViews.custom.length > 0 && (
              <optgroup label="Saved views">
                {groupedViews.custom.map((view) => (
                  <option key={view.viewId} value={view.viewId}>
                    {formatViewCatalogLabel(view)}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </label>

        {activeView && (activeView.scopeTypes?.length ?? 0) > 1 && (
          <label className="form-field">
            <span className="form-field__label">Scope type</span>
            <select
              className="select"
              value={scopeType}
              onChange={(event) => {
                const nextScopeType = event.target.value;
                const nextScopeId = nextScopeType === 'site' ? selectedSiteId : nextScopeType === 'org' ? (orgId ?? '') : '';
                if (
                  guardPendingWorkbenchSwitch({
                    scopeType: nextScopeType,
                    scopeId: nextScopeId || undefined
                  })
                ) {
                  return;
                }
                setScopeType(nextScopeType);
                setScopeIdInput('');
              }}
              disabled={isBoundScopeLocked}
            >
              {(activeView.scopeTypes ?? []).map((scope) => (
                <option key={scope} value={scope}>
                  {formatScopeLabel(scope)}
                </option>
              ))}
            </select>
          </label>
        )}

        {scopeType === 'site' && (
          <label className="form-field">
            <span className="form-field__label">Site</span>
            <select
              className="select"
              value={selectedSiteId}
              onChange={(event) => {
                if (guardPendingWorkbenchSwitch({ scopeId: event.target.value })) {
                  return;
                }
                setSelectedSiteId(event.target.value);
              }}
              disabled={isBoundScopeLocked || sitesLoading || sortedSites.length === 0}
            >
              <option value="">{sitesLoading ? 'Loading sites…' : 'Select site…'}</option>
              {sortedSites.map((site) => (
                <option key={site.id ?? site.name ?? 'site-option'} value={site.id ?? ''}>
                  {site.name ?? site.id}
                </option>
              ))}
            </select>
          </label>
        )}

        {scopeType === 'org' && (
          <label className="form-field">
            <span className="form-field__label">Organization</span>
            <input type="text" className="input" value={orgDisplayName} readOnly />
          </label>
        )}

        {scopeType !== 'site' && scopeType !== 'org' && (
          <label className="form-field">
            <span className="form-field__label">{`${formatScopeLabel(scopeType)} ID`}</span>
            <input
              type="text"
              className="input"
              value={scopeIdInput}
              onChange={(event) => {
                if (guardPendingWorkbenchSwitch({ scopeId: event.target.value })) {
                  return;
                }
                setScopeIdInput(event.target.value);
              }}
              disabled={isBoundScopeLocked}
              placeholder={`Enter ${formatScopeLabel(scopeType)} id`}
            />
          </label>
        )}

        <label className="form-field topology-workbench__time-field">
          <span className="form-field__label">Time</span>
          <div className="topology-workbench__time-controls">
            <select
              className="select"
              value={timeMode}
              onChange={(event) => {
                pendingLocalAsOfSyncRef.current = true;
                setTimeMode(event.target.value === 'fixed' ? 'fixed' : 'live');
              }}
            >
              <option value="live">Live</option>
              <option value="fixed">As of</option>
            </select>
            {timeMode === 'fixed' && (
              <input
                type="datetime-local"
                className="input"
                value={asOfInput}
                step={1}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  pendingLocalAsOfSyncRef.current = true;
                  setAsOfInput(nextValue);
                  setAsOfValue(toIsoString(nextValue));
                }}
                aria-label="As of timestamp"
              />
            )}
          </div>
        </label>
      </div>

      {!embedded && (
        <div className="topology-workbench__toolbar-actions">
          <span className="topology-workbench__toolbar-status" title={timeStatusTitle}>
            {timeStatusLabel}
          </span>
          {workspaceStatusLabel && (
            <span className="topology-workbench__toolbar-status" title={workspaceStatusTitle}>
              {workspaceStatusLabel}
            </span>
          )}
          <div className="topology-workbench__toolbar-icon-group">
            <button
              type="button"
              className="drawer-icon-button topology-workbench__toolbar-icon"
              onClick={() => setSaveDrawerOpen(true)}
              disabled={!activeView}
              aria-label={isCustomSelectedView ? 'Saved view actions' : 'Save as custom view'}
              title={isCustomSelectedView ? 'Saved view actions' : 'Save as custom view'}
            >
              <SaveIcon size={18} />
            </button>
            {isCustomSelectedView && (
              <button
                type="button"
                className="drawer-icon-button drawer-icon-button--danger topology-workbench__toolbar-icon"
                onClick={() => setDeleteViewConfirmationOpen(true)}
                disabled={savedViewBusy}
                aria-label="Delete saved view"
                title="Delete saved view"
              >
                <TrashIcon size={18} />
              </button>
            )}
            <button
              type="button"
              className="drawer-icon-button topology-workbench__toolbar-icon topology-workbench__toolbar-icon--layers"
              onClick={() => setLayersDrawerOpen(true)}
              aria-label={`Layers (${totalLayerCount})`}
              title={`Layers (${totalLayerCount})`}
            >
              <LayersIcon size={18} />
              <span className="topology-workbench__toolbar-count">{totalLayerCount}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const paramsPanel = !hideControls && activeView && visibleParams.length > 0 && (
    <details className="section-card form-grid topology-v2-params topology-workbench__params-panel" open={visibleParams.length <= 2}>
      <summary className="section-card__title">View parameters</summary>
      {visibleParams.map(([name, spec]) => (
        <label key={name} className="form-field">
          <span className="form-field__label">
            {viewParamLabel(name, spec)}
            {spec.required ? ' *' : ''}
          </span>
          {renderParamControl(spec, viewParams[name], effectiveParamOptions[name], (value) => {
            const nextParams = nextParamValuesAfterChange(activeView, viewParams, name, value);
            if (guardPendingWorkbenchSwitch({ params: nextParams })) {
              return;
            }
            setViewParams(nextParams);
          })}
          {effectiveParamOptions[name]?.error && <span className="form-field__hint">{effectiveParamOptions[name]?.error}</span>}
          {spec.hint && <span className="form-field__hint">{spec.hint}</span>}
        </label>
      ))}
    </details>
  );

  const workbenchNotice =
    !embedded && (workbenchError || workbenchRestoring) ? (
      <div className={`section-card topology-workbench__notice${workbenchError ? ' topology-workbench__notice--error' : ''}`}>
        {workbenchError ?? 'Restoring topology workbench session…'}
      </div>
    ) : null;

  const topologyCanvas = renderedTopologyGraph ? (
    <div className="topology-diagram topology-workbench__diagram">
      {activeRelatedView && (
        <div className="topology-workbench__related-view">
          <span>Path detail</span>
          <strong>{activeRelatedView.label}</strong>
          {activeRelatedView.sourceViewId && <em>from {activeRelatedView.sourceViewId}</em>}
          {pendingProposalId && activeRelatedView.sourceViewId && (
            <button
              type="button"
              className="topology-workbench__related-view-back"
              onClick={handleRejectProposal}
              disabled={Boolean(workbenchBusyAction)}
              title={`Discard this preview and return to ${activeRelatedView.sourceViewId}`}
            >
              {workbenchBusyAction === 'reject' ? 'Returning…' : `Back to ${activeRelatedView.sourceViewId}`}
            </button>
          )}
        </div>
      )}
      <TopologyGraphRenderer
        topology={renderedTopologyGraph}
        graphKey={`${topologyDiagramKey}:${activeRelatedView?.layerId ?? 'base'}`}
        pickNodeUI={pickNodeUI}
        pickEdgeUI={pickEdgeUI}
        overlayKeyByNodeId={merged?.overlayKeyByNodeId ?? {}}
        overlayKeyByEdgeId={merged?.overlayKeyByEdgeId ?? {}}
        selectedNodeIds={selectedNodeIds}
        selectedEdgeIds={selectedEdgeIds}
        onNodeSelect={handleNodeSelect}
        onEdgeSelect={handleEdgeSelect}
        onNodeDoubleClick={handleNodeDoubleClick}
        onEdgeDoubleClick={handleEdgeDoubleClick}
        onPaneClick={handlePaneClick}
        requestTopologyAction={requestTopologyAction}
      />
      {selectionPanel}
    </div>
  ) : (
    <div className="topology-workbench__empty-state">
      <OrgModulePlaceholder
        intent={
          resolveLoading
            ? 'Resolving topology…'
            : boundScopeError
              ? boundScopeError
              : resolveError
                ? resolveError
              : 'No topology resolved yet.'
        }
      />
    </div>
  );

  const workbenchActionStrip =
    pendingProposalId || hasUndoableHistory || hasAbortableSession ? (
      <section className="section-card topology-workbench__action-strip">
        <div className="topology-workbench__action-copy">
          <h2 className="topology-workbench__proposal-title">{workbenchActionTitle}</h2>
          <p className="topology-workbench__proposal-subtitle">{workbenchActionSubtitle}</p>
        </div>
        <div className="topology-workbench__proposal-actions">
          {pendingProposalId ? (
            <>
              <button
                type="button"
                className="button"
                onClick={handleApplyProposal}
                disabled={Boolean(workbenchBusyAction)}
              >
                {workbenchBusyAction === 'apply' ? 'Applying…' : 'Apply'}
              </button>
              <button
                type="button"
                className="button button--secondary"
                onClick={handleRejectProposal}
                disabled={Boolean(workbenchBusyAction)}
              >
                {workbenchBusyAction === 'reject' ? 'Discarding…' : 'Discard preview'}
              </button>
            </>
          ) : hasUndoableHistory ? (
            <button
              type="button"
              className="button button--secondary"
              onClick={handleUndoProposal}
              disabled={Boolean(workbenchBusyAction)}
            >
              {workbenchBusyAction === 'undo' ? 'Undoing…' : 'Undo last change'}
            </button>
          ) : null}
          {hasAbortableSession ? (
            <button
              type="button"
              className="button button--secondary"
              onClick={handleDiscardSession}
              disabled={Boolean(workbenchBusyAction)}
            >
              {workbenchBusyAction === 'discard-session' ? 'Aborting…' : 'Abort session'}
            </button>
          ) : null}
        </div>
      </section>
    ) : null;

  const chatBlockedActions = pendingProposalId ? (
    <>
      <button
        type="button"
        className="button button--small"
        onClick={handleApplyProposal}
        disabled={Boolean(workbenchBusyAction)}
      >
        {workbenchBusyAction === 'apply' ? 'Applying…' : 'Apply preview'}
      </button>
      <button
        type="button"
        className="button button--secondary button--small"
        onClick={handleRejectProposal}
        disabled={Boolean(workbenchBusyAction)}
      >
        {workbenchBusyAction === 'reject' ? 'Discarding…' : 'Discard preview'}
      </button>
    </>
  ) : null;

  const layersDrawerBody =
    totalLayerCount === 0 ? (
      <p className="topology-workbench__layers-empty">No layers are currently active for this view.</p>
    ) : (
      <div className="topology-workbench__layers-groups">
        {renderLayerGroup('Saved view layers', savedOverlayState.layers, savedHiddenLayerIds, savedOverlayState, toggleSavedLayerVisibility)}
        {renderLayerGroup('Applied changes', appliedWorkbenchLayers, workbenchHiddenLayerIds, workbenchOverlayState, toggleWorkbenchLayerVisibility)}
        {renderLayerGroup('Preview', previewWorkbenchLayers, workbenchHiddenLayerIds, workbenchOverlayState, toggleWorkbenchLayerVisibility)}
      </div>
    );

  const saveDrawerFooter = activeView ? (
    <div className="topology-workbench__drawer-footer">
      <button
        type="button"
        className="button button--ghost"
        onClick={() => setSaveDrawerOpen(false)}
        disabled={savedViewBusy}
      >
        Close
      </button>
      {isCustomSelectedView && (
        <button
          type="button"
          className="button button--secondary"
          onClick={() => void handleCreateCustomView()}
          disabled={savedViewBusy || !canSaveFromWorkbench || !savedViewName.trim()}
        >
          {savedViewBusyAction === 'create' ? 'Saving copy…' : 'Save as copy'}
        </button>
      )}
      {isCustomSelectedView ? (
        <>
          <button
            type="button"
            className="button button--secondary"
            onClick={() => void handleUpdateCustomViewMetadata()}
            disabled={savedViewBusy || !savedViewName.trim()}
          >
            {savedViewBusyAction === 'update-metadata' ? 'Updating…' : 'Update details'}
          </button>
          <button
            type="button"
            className="button"
            onClick={() => void handleReplaceCustomView()}
            disabled={savedViewBusy || !canSaveFromWorkbench || !savedViewName.trim()}
          >
            {savedViewBusyAction === 'replace' ? 'Saving changes…' : 'Save changes'}
          </button>
        </>
      ) : (
        <button
          type="button"
          className="button"
          onClick={() => void handleCreateCustomView()}
          disabled={savedViewBusy || !canSaveFromWorkbench || !savedViewName.trim()}
        >
          {savedViewBusyAction === 'create' ? 'Saving…' : 'Save as custom view'}
        </button>
      )}
    </div>
  ) : undefined;

  const dirtySwitchDrawerFooter = pendingWorkbenchSwitch ? (
    <div className="topology-workbench__drawer-footer">
      <button
        type="button"
        className="button button--ghost"
        onClick={handleStayOnCurrentWorkbench}
        disabled={Boolean(workbenchBusyAction)}
      >
        Stay here
      </button>
      <button
        type="button"
        className="button button--secondary"
        onClick={handleSaveBeforeSwitch}
        disabled={Boolean(workbenchBusyAction)}
      >
        {pendingSwitchSaveLabel}
      </button>
      <button
        type="button"
        className="button"
        onClick={handleDiscardSessionAndSwitch}
        disabled={Boolean(workbenchBusyAction)}
      >
        {workbenchBusyAction === 'discard-session' ? 'Discarding…' : 'Discard session and switch'}
      </button>
    </div>
  ) : undefined;

  const mainContent = (
    <>
      {controlFields}
      {paramsPanel}
      {workbenchNotice}
      {topologyCanvas}
    </>
  );

  const content = embedded ? (
    mainContent
  ) : (
    <>
      <div className="topology-workbench">
        {controlFields}
        {paramsPanel}
        {workbenchActionStrip}
        <div
          ref={workspaceRef}
          className={`topology-workbench__workspace topology-workbench__workspace--${workspaceMode}${isResizingWorkspace ? ' topology-workbench__workspace--resizing' : ''}`}
        >
          <section className="topology-workbench__pane topology-workbench__pane--topology">
            {workbenchNotice}
            {topologyCanvas}
          </section>

          <div className="topology-workbench__divider">
            <div className="topology-workbench__divider-controls">
              <button
                type="button"
                className="drawer-icon-button topology-workbench__divider-button"
                onClick={() => setWorkspaceMode('topology')}
                disabled={workspaceMode === 'topology'}
                aria-label="Collapse chat"
                title="Collapse chat"
              >
                <GraphIcon size={18} />
              </button>
              <button
                type="button"
                className="drawer-icon-button topology-workbench__divider-button"
                onClick={resetWorkspaceLayout}
                disabled={workspaceMode === 'split' && chatPaneWidth === DEFAULT_CHAT_PANE_WIDTH}
                aria-label="Reset split"
                title="Reset split"
              >
                <ExpandIcon size={18} />
              </button>
              <button
                type="button"
                className="drawer-icon-button topology-workbench__divider-button"
                onClick={() => setWorkspaceMode('chat')}
                disabled={workspaceMode === 'chat'}
                aria-label="Collapse topology"
                title="Collapse topology"
              >
                <ChatIcon size={18} />
              </button>
            </div>
            <button
              type="button"
              className="topology-workbench__divider-grip"
              onMouseDown={handleWorkspaceResizeStart}
              onDoubleClick={resetWorkspaceLayout}
              aria-label="Resize panes"
              title="Drag to resize. Double-click to reset."
            />
          </div>

          <aside
            className="topology-workbench__pane topology-workbench__pane--chat"
            style={workspaceMode === 'split' ? { flexBasis: `${chatPaneWidth}px` } : undefined}
          >
            <TopologyWorkbenchChatPanel
              orgId={orgId}
              basePath={basePath}
              threadId={workbenchThreadId}
              session={workbenchSession}
              requestOrgApi={requestOrgApi}
              ensureSession={ensureWorkbenchSession}
              buildInteractContext={buildWorkbenchInteractContext}
              onThreadIdChange={setWorkbenchThreadId}
              onSessionRefreshRequested={refreshWorkbenchSession}
              disabled={Boolean(chatBlockedReason)}
              disabledReason={chatBlockedReason}
              disabledActions={chatBlockedActions}
              title="Chat"
              subtitle={
                pendingProposalId
                  ? 'Preview actions are blocked until the current change is resolved.'
                  : isCustomSelectedView
                    ? 'Ask about the selected saved topology view.'
                    : 'Ask about the current topology surface.'
              }
            />
          </aside>
        </div>
      </div>

      <Drawer
        open={layersDrawerOpen}
        title="Layers"
        subtitle="Saved layers, applied changes, and preview overlays for the current surface."
        onClose={() => setLayersDrawerOpen(false)}
      >
        {layersDrawerBody}
      </Drawer>

      <Drawer
        open={saveDrawerOpen}
        title={isCustomSelectedView ? 'Saved view' : 'Save as custom view'}
        subtitle={
          isCustomSelectedView
            ? `Private ${savedViewScopeLabel.toLowerCase()} view.`
            : 'Save the current workbench state as a private custom topology view.'
        }
        onClose={() => setSaveDrawerOpen(false)}
        footer={saveDrawerFooter}
      >
        {activeView ? (
          <div className="topology-workbench__saved-view-drawer">
            {isCustomSelectedView && (
              <div className="topology-workbench__saved-view-meta">
                <span>Owner: {activeView.ownerUserId ? `${activeView.ownerUserId.slice(0, 8)}…` : 'me'}</span>
                <span>Scope mode: {savedViewScopeLabel}</span>
                {activeView.boundScope && (
                  <span>
                    Bound scope: {activeView.boundScope.scopeType}:{activeView.boundScope.scopeId}
                  </span>
                )}
                {activeView.baseTemplateViewId && <span>Base view: {activeView.baseTemplateViewId}</span>}
              </div>
            )}

            <div className="topology-workbench__saved-view-form">
              <label className="form-field">
                <span className="form-field__label">Name</span>
                <input
                  type="text"
                  className="input"
                  value={savedViewName}
                  onChange={(event) => setSavedViewName(event.target.value)}
                  placeholder="Saved view name"
                />
              </label>
              <label className="form-field">
                <span className="form-field__label">Description</span>
                <textarea
                  className="textarea"
                  rows={3}
                  value={savedViewDescription}
                  onChange={(event) => setSavedViewDescription(event.target.value)}
                  placeholder="Optional description"
                />
              </label>
              <label className="form-field">
                <span className="form-field__label">{isCustomSelectedView ? 'Replace mode' : 'Save mode'}</span>
                <select
                  className="select"
                  value={savedViewMode}
                  onChange={(event) => setSavedViewMode(event.target.value as TopologyV2ViewScopeModeschema)}
                >
                  <option value="reusable">Reusable</option>
                  <option value="current_scope">Current scope</option>
                </select>
              </label>
              {!canSaveFromWorkbench && (
                <p className="topology-workbench__saved-view-hint">
                  {pendingProposalId
                    ? 'Apply or discard the current preview before saving.'
                    : workbenchSession?.id
                      ? 'Resolve the topology again if you need a newer workbench snapshot.'
                      : 'Start the topology workbench and apply changes before saving.'}
                </p>
              )}
            </div>

            {savedViewError && (
              <div className="topology-workbench__notice topology-workbench__notice--error">{savedViewError}</div>
            )}

            {savedViewResult && (
              <div className="topology-workbench__saved-view-feedback">
                {savedViewSummaryLines.length > 0 && (
                  <div className="topology-workbench__saved-view-summary">
                    {savedViewSummaryLines.map((line) => (
                      <span key={line}>{line}</span>
                    ))}
                  </div>
                )}
                {(savedViewResult.warnings ?? []).length > 0 && (
                  <div className="topology-workbench__saved-view-warnings">
                    {(savedViewResult.warnings ?? []).map((warning) => (
                      <div key={`${warning.code}:${warning.layerId ?? 'global'}`} className="topology-workbench__saved-view-warning">
                        <strong>{warning.code}</strong>: {warning.message}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <p className="topology-workbench__saved-view-hint">Select a topology view to save or update it.</p>
        )}
      </Drawer>

      <Drawer
        open={Boolean(pendingWorkbenchSwitch)}
        title="Leave current session?"
        subtitle="This switch would leave unsaved session changes on the current base view."
        onClose={handleStayOnCurrentWorkbench}
        footer={dirtySwitchDrawerFooter}
      >
        {pendingWorkbenchSwitch && (
          <div className="topology-workbench__saved-view-drawer">
            <p className="topology-workbench__saved-view-hint">
              Switching now will discard the current session unless you save it first.
            </p>
            <div className="topology-workbench__saved-view-meta">
              <span>Current base view: {activeView?.name ?? activeView?.viewId ?? selectedViewId}</span>
              <span>Requested view: {pendingSwitchView?.name ?? pendingWorkbenchSwitch.viewId}</span>
              <span>Requested scope: {pendingSwitchScopeLabel}</span>
            </div>
          </div>
        )}
      </Drawer>
      <DeleteConfirmationModal
        open={deleteViewConfirmationOpen}
        title="Delete saved view?"
        description={`Delete saved view "${activeView?.name ?? activeView?.viewId ?? ''}"? This cannot be undone.`}
        deleting={savedViewBusyAction === 'delete'}
        onCancel={() => setDeleteViewConfirmationOpen(false)}
        onConfirm={() => void handleDeleteCustomView()}
      />
      {deleteToast && <StatusToast tone={deleteToast.tone} message={deleteToast.message} occurrence={deleteToast} onDismiss={() => setDeleteToast(null)} />}
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <OrgModuleLayout
      title="Topology v2"
      description=""
      topSlot={topSlot}
    >
      {content}
    </OrgModuleLayout>
  );
};

const renderParamControl = (
  spec: TopologyV2ParamSpec,
  value: ParamValue | undefined,
  optionsState: ParamOptionsState | undefined,
  onChange: (next: ParamValue) => void
) => {
  const placeholder = typeof spec.ui?.placeholder === 'string' ? spec.ui?.placeholder : undefined;
  if (spec.options) {
    const items = optionsState?.items ?? [];
    const loading = optionsState?.loading ?? false;
    const blocked = optionsState?.blocked ?? false;
    const errored = Boolean(optionsState?.error);
    const disabled = blocked || loading || errored || items.length === 0;
    const selectedValue =
      typeof value === 'string' && items.some((option) => option.value === value) ? value : '';
    let emptyLabel = typeof optionsState?.emptyLabel === 'string' && optionsState.emptyLabel.trim() ? optionsState.emptyLabel : 'Select option…';
    if (blocked && emptyLabel === 'Select option…') {
      emptyLabel = 'Select required filters first…';
    } else if (loading && emptyLabel === 'Select option…') {
      emptyLabel = 'Loading options…';
    } else if (errored && emptyLabel === 'Select option…') {
      emptyLabel = 'Failed to load options';
    } else if (items.length === 0 && emptyLabel === 'Select option…') {
      emptyLabel = 'No options available';
    }

    return (
      <select
        className="select"
        value={selectedValue}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
      >
        <option value="">{emptyLabel}</option>
        {items.map((option) => (
          <option key={`${option.value}-${option.label}`} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  if (spec.type === 'boolean') {
    return (
      <label className="checkbox-row">
        <input
          type="checkbox"
          className="checkbox"
          checked={Boolean(value)}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span>{placeholder ?? 'Enabled'}</span>
      </label>
    );
  }

  if (spec.type === 'number' || spec.type === 'integer') {
    return (
      <input
        type="number"
        className="input"
        value={typeof value === 'string' ? value : ''}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
      />
    );
  }

  if (spec.type === 'object') {
    return (
      <textarea
        className="textarea"
        value={typeof value === 'string' ? value : ''}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder ?? '{ }'}
        rows={3}
      />
    );
  }

  return (
    <input
      type="text"
      className="input"
      value={typeof value === 'string' ? value : ''}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
    />
  );
};

const buildDefaultParams = (view: TopologyV2ViewCatalogItem): Record<string, ParamValue> => {
  const params: Record<string, ParamValue> = {};
  Object.entries(view.params ?? {}).forEach(([name, spec]) => {
    const defaultValue = (spec as { _default?: unknown })._default;
    if (spec.type === 'boolean') {
      if (typeof defaultValue === 'boolean') {
        params[name] = defaultValue;
        return;
      }
      params[name] = false;
      return;
    }
    if (defaultValue !== undefined && defaultValue !== null) {
      if (spec.type === 'object') {
        params[name] = JSON.stringify(defaultValue);
        return;
      }
      params[name] = String(defaultValue);
      return;
    }
    params[name] = '';
  });
  return params;
};

const resolveParams = (
  view: TopologyV2ViewCatalogItem,
  values: Record<string, ParamValue>
): { params: Record<string, unknown>; errors: string[] } => {
  const params: Record<string, unknown> = {};
  const errors: string[] = [];

  Object.entries(view.params ?? {}).forEach(([name, spec]) => {
    const raw = values[name];
    const isEmpty = raw === undefined || raw === null || raw === '';
    if (spec.required && isEmpty) {
      errors.push(`Param ${name} is required.`);
      return;
    }
    if (isEmpty) {
      return;
    }
    if (spec.type === 'boolean') {
      if (typeof raw === 'string') {
        params[name] = raw.toLowerCase() === 'true';
      } else {
        params[name] = Boolean(raw);
      }
      return;
    }
    if (spec.type === 'number' || spec.type === 'integer') {
      const parsed = Number(raw);
      if (Number.isNaN(parsed)) {
        errors.push(`Param ${name} must be a number.`);
        return;
      }
      params[name] = spec.type === 'integer' ? Math.trunc(parsed) : parsed;
      return;
    }
    if (spec.type === 'object') {
      try {
        params[name] = JSON.parse(String(raw));
      } catch {
        errors.push(`Param ${name} must be valid JSON.`);
      }
      return;
    }
    params[name] = raw;
  });

  return { params, errors };
};

function formatScopeLabel(scope: string): string {
  const normalized = scope.trim().toLowerCase();
  if (normalized === 'org') {
    return 'Organization';
  }
  if (normalized === 'site') {
    return 'Site';
  }
  if (normalized === 'device') {
    return 'Device';
  }
  return normalized.length > 0 ? normalized[0].toUpperCase() + normalized.slice(1) : 'Scope';
}

function formatViewCatalogLabel(view: TopologyV2ViewCatalogItem): string {
  const viewId = view.viewId ?? '';
  const name = view.name ?? viewId;
  const suffix =
    view.origin === 'custom'
      ? view.scopeMode === 'current_scope'
        ? ' [Current scope]'
        : ' [Saved]'
      : '';
  if (viewId === 'org_overview' || viewId === 'org_site_pair_links') {
    return `${name || 'Intersite Topology'}${suffix}`;
  }
  if (viewId === 'aws_cloud_overview') {
    return `${name || 'AWS Cloud'}${suffix}`;
  }
  return view.description ? `${name}${suffix} - ${view.description}` : `${name}${suffix}`;
}

export default TopologyV2Page;

function formatPolicyRawJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function TopologyPolicyRawDetails({ value }: { value: unknown }) {
  const [open, setOpen] = useState(false);
  const rawJson = useMemo(() => (open ? formatPolicyRawJson(value) : ''), [open, value]);

  return (
    <details
      className="topology-v2__policy-raw"
      onToggle={(event) => {
        setOpen(event.currentTarget.open);
      }}
    >
      <summary>Raw rule JSON</summary>
      {open ? <pre>{rawJson}</pre> : null}
    </details>
  );
}
